/**
 * Client-safe enum constants
 *
 * This file exports enum value objects that can be safely imported in client-side code.
 * It does NOT import any database drivers (postgres) or server-only dependencies.
 *
 * @example
 * // In client-side code
 * import { DeletionStatus, InterviewStatus } from '@repo/db/constants';
 */

export { createId } from "@paralleldrive/cuid2";

// Ad Creatives
export const AdCreativeSetStatus = {
  GENERATING: "GENERATING",
  REVIEW: "REVIEW",
  APPROVED: "APPROVED",
  PUBLISHED: "PUBLISHED",
  PAUSED: "PAUSED",
  STOPPED: "STOPPED",
  FAILED: "FAILED",
} as const;
export type AdCreativeSetStatus =
  (typeof AdCreativeSetStatus)[keyof typeof AdCreativeSetStatus];

export const AdPlatform = {
  META: "META",
  LINKEDIN: "LINKEDIN",
} as const;
export type AdPlatform = (typeof AdPlatform)[keyof typeof AdPlatform];

export const AdCreativeFormat = {
  IMAGE: "IMAGE",
  VIDEO: "VIDEO",
} as const;
export type AdCreativeFormat =
  (typeof AdCreativeFormat)[keyof typeof AdCreativeFormat];

export const AdCreativeVariationStatus = {
  DRAFT: "DRAFT",
  APPROVED: "APPROVED",
  REJECTED: "REJECTED",
  FAILED: "FAILED",
} as const;
export type AdCreativeVariationStatus =
  (typeof AdCreativeVariationStatus)[keyof typeof AdCreativeVariationStatus];

export const OutboundLeadStatus = {
  PENDING: "PENDING",
  SENT: "SENT",
  OPENED: "OPENED",
  CLICKED: "CLICKED",
  REPLIED: "REPLIED",
  BOUNCED: "BOUNCED",
  UNSUBSCRIBED: "UNSUBSCRIBED",
  FILTERED_OUT: "FILTERED_OUT",
} as const;
export type OutboundLeadStatus =
  (typeof OutboundLeadStatus)[keyof typeof OutboundLeadStatus];

export const OutboundSuppressionReason = {
  UNSUBSCRIBED: "UNSUBSCRIBED",
  DO_NOT_CONTACT: "DO_NOT_CONTACT",
  NOT_INTERESTED: "NOT_INTERESTED",
  OPT_OUT_REPLY: "OPT_OUT_REPLY",
  MANUAL: "MANUAL",
} as const;
export type OutboundSuppressionReason =
  (typeof OutboundSuppressionReason)[keyof typeof OutboundSuppressionReason];

export const ProspectClassification = {
  GOOD: "GOOD",
  MAYBE: "MAYBE",
  BAD: "BAD",
} as const;
export type ProspectClassification =
  (typeof ProspectClassification)[keyof typeof ProspectClassification];

// Organization & Billing
export const OrganizationEnvironment = {
  PRODUCTION: "PRODUCTION",
  DEMO: "DEMO",
} as const;
export type OrganizationEnvironment =
  (typeof OrganizationEnvironment)[keyof typeof OrganizationEnvironment];

/**
 * Check if an organization is a demo organization.
 * Demo organizations bypass billing, recruitment, payouts, and notifications.
 */
export function isDemoEnvironment(
  environment: string | null | undefined,
): boolean {
  return environment === OrganizationEnvironment.DEMO;
}

export const TERAC_DEV_ORGANIZATION_ID = "10c24011-8f7d-40ff-baac-f067c606cb73";

export const AutoTopupStatus = {
  ENABLED: "ENABLED",
  DISABLED: "DISABLED",
  PAUSED: "PAUSED",
} as const;
export type AutoTopupStatus =
  (typeof AutoTopupStatus)[keyof typeof AutoTopupStatus];

export const DemoInterviewAvailability = {
  ENABLED: "ENABLED",
  DISABLED: "DISABLED",
} as const;
export type DemoInterviewAvailability =
  (typeof DemoInterviewAvailability)[keyof typeof DemoInterviewAvailability];

export const PlatformFeeType = {
  PERCENTAGE: "PERCENTAGE",
  FLAT: "FLAT",
} as const;
export type PlatformFeeType =
  (typeof PlatformFeeType)[keyof typeof PlatformFeeType];

export const SlackConnectionStatus = {
  NOT_CONNECTED: "NOT_CONNECTED",
  CONNECTED: "CONNECTED",
  DISCONNECTED: "DISCONNECTED",
} as const;
export type SlackConnectionStatus =
  (typeof SlackConnectionStatus)[keyof typeof SlackConnectionStatus];

// User Status
export const DeletionStatus = {
  ACTIVE: "ACTIVE",
  DELETED: "DELETED",
} as const;
export type DeletionStatus =
  (typeof DeletionStatus)[keyof typeof DeletionStatus];

// Per-submission email-forwarding alias state.
export const AliasState = {
  ACTIVE: "ACTIVE",
  DISABLED: "DISABLED",
  PURGED: "PURGED",
} as const;
export type AliasState = (typeof AliasState)[keyof typeof AliasState];

// ADMIN is sticky against the reconciler; LIFECYCLE is reversible.
export const AliasDisableSource = {
  LIFECYCLE: "LIFECYCLE",
  ADMIN: "ADMIN",
} as const;
export type AliasDisableSource =
  (typeof AliasDisableSource)[keyof typeof AliasDisableSource];

export const BanStatus = {
  ACTIVE: "ACTIVE",
  BANNED: "BANNED",
  SUSPENDED: "SUSPENDED",
} as const;
export type BanStatus = (typeof BanStatus)[keyof typeof BanStatus];

export const BusinessType = {
  B2B: "B2B",
  B2C: "B2C",
} as const;
export type BusinessType = (typeof BusinessType)[keyof typeof BusinessType];

// Identity face matching. Mirrored here because the review UI is a client
// component and @repo/db is server-only.
export const IdentityFaceOutcome = {
  MATCH: "MATCH",
  REVIEW: "REVIEW",
  MISMATCH: "MISMATCH",
  UNCHECKABLE: "UNCHECKABLE",
} as const;
export type IdentityFaceOutcome =
  (typeof IdentityFaceOutcome)[keyof typeof IdentityFaceOutcome];

export const IdentityFaceReferenceTrust = {
  AUTHORITATIVE: "AUTHORITATIVE",
  SELF_ASSERTED: "SELF_ASSERTED",
} as const;
export type IdentityFaceReferenceTrust =
  (typeof IdentityFaceReferenceTrust)[keyof typeof IdentityFaceReferenceTrust];

export const IdentityFaceReviewDecision = {
  CLEARED: "CLEARED",
  CONFIRMED_MISMATCH: "CONFIRMED_MISMATCH",
} as const;
export type IdentityFaceReviewDecision =
  (typeof IdentityFaceReviewDecision)[keyof typeof IdentityFaceReviewDecision];

export const IdentityFaceSubjectType = {
  SCREENING_INTERVIEW_SESSION: "SCREENING_INTERVIEW_SESSION",
  ONBOARDING_INTERVIEW_SESSION: "ONBOARDING_INTERVIEW_SESSION",
  TASK_OBSERVATION: "TASK_OBSERVATION",
} as const;
export type IdentityFaceSubjectType =
  (typeof IdentityFaceSubjectType)[keyof typeof IdentityFaceSubjectType];

export const InternalPriority = {
  P0: "P0",
  P1: "P1",
  P2: "P2",
} as const;
export type InternalPriority =
  (typeof InternalPriority)[keyof typeof InternalPriority];

export const LockStatus = {
  UNLOCKED: "UNLOCKED",
  LOCKED: "LOCKED",
  TEMPORARILY_LOCKED: "TEMPORARILY_LOCKED",
} as const;
export type LockStatus = (typeof LockStatus)[keyof typeof LockStatus];

// The onboarding gate: COMPLETED means the user finished the /onboarding
// wizard. Use this for redirect/gating logic.
export const OnboardingFlowStatus = {
  NOT_STARTED: "NOT_STARTED",
  IN_PROGRESS: "IN_PROGRESS",
  COMPLETED: "COMPLETED",
} as const;
export type OnboardingFlowStatus =
  (typeof OnboardingFlowStatus)[keyof typeof OnboardingFlowStatus];

export const OnboardingInterviewStatus = {
  NOT_STARTED: "NOT_STARTED",
  COMPLETED: "COMPLETED",
} as const;
export type OnboardingInterviewStatus =
  (typeof OnboardingInterviewStatus)[keyof typeof OnboardingInterviewStatus];

export const TermsAcceptanceStatus = {
  NOT_ACCEPTED: "NOT_ACCEPTED",
  ACCEPTED: "ACCEPTED",
} as const;
export type TermsAcceptanceStatus =
  (typeof TermsAcceptanceStatus)[keyof typeof TermsAcceptanceStatus];

export const TwoFactorVerificationStatus = {
  NOT_VERIFIED: "NOT_VERIFIED",
  VERIFIED: "VERIFIED",
} as const;
export type TwoFactorVerificationStatus =
  (typeof TwoFactorVerificationStatus)[keyof typeof TwoFactorVerificationStatus];

export const VerificationStatus = {
  UNVERIFIED: "UNVERIFIED",
  PENDING: "PENDING",
  PROCESSING: "PROCESSING",
  VERIFIED: "VERIFIED",
  FAILED: "FAILED",
  CANCELED: "CANCELED",
  REDACTED: "REDACTED",
  DUPLICATE_DETECTED: "DUPLICATE_DETECTED",
  NEEDS_REVIEW: "NEEDS_REVIEW",
} as const;
export type VerificationStatus =
  (typeof VerificationStatus)[keyof typeof VerificationStatus];

export const VerificationProvider = {
  STRIPE: "STRIPE",
  PERSONA: "PERSONA",
  WHOP: "WHOP",
} as const;
export type VerificationProvider =
  (typeof VerificationProvider)[keyof typeof VerificationProvider];

export const PersonaInquiryStatus = {
  CREATED: "created",
  PENDING: "pending",
  COMPLETED: "completed",
  NEEDS_REVIEW: "needs_review",
  APPROVED: "approved",
  DECLINED: "declined",
  FAILED: "failed",
  EXPIRED: "expired",
} as const;
export type PersonaInquiryStatus =
  (typeof PersonaInquiryStatus)[keyof typeof PersonaInquiryStatus];

// Onboarding insights target for feed visibility
export const ONBOARDING_INSIGHTS_TARGET = 10;

// Stripe Identity verification error codes
// See: https://docs.stripe.com/api/identity/verification_sessions/object#identity_verification_session_object-last_error-code
export const StripeIdentityErrorCode = {
  CONSENT_DECLINED: "consent_declined",
  COUNTRY_NOT_SUPPORTED: "country_not_supported",
  DEVICE_NOT_SUPPORTED: "device_not_supported",
  DOCUMENT_EXPIRED: "document_expired",
  DOCUMENT_TYPE_NOT_SUPPORTED: "document_type_not_supported",
  DOCUMENT_UNVERIFIED_OTHER: "document_unverified_other",
  EMAIL_UNVERIFIED_OTHER: "email_unverified_other",
  EMAIL_VERIFICATION_DECLINED: "email_verification_declined",
  ID_NUMBER_INSUFFICIENT_DOCUMENT_DATA: "id_number_insufficient_document_data",
  ID_NUMBER_MISMATCH: "id_number_mismatch",
  ID_NUMBER_UNVERIFIED_OTHER: "id_number_unverified_other",
  PHONE_UNVERIFIED_OTHER: "phone_unverified_other",
  PHONE_VERIFICATION_DECLINED: "phone_verification_declined",
  SELFIE_DOCUMENT_MISSING_PHOTO: "selfie_document_missing_photo",
  SELFIE_FACE_MISMATCH: "selfie_face_mismatch",
  SELFIE_MANIPULATED: "selfie_manipulated",
  SELFIE_UNVERIFIED_OTHER: "selfie_unverified_other",
  UNDER_SUPPORTED_AGE: "under_supported_age",
} as const;
export type StripeIdentityErrorCode =
  (typeof StripeIdentityErrorCode)[keyof typeof StripeIdentityErrorCode];

// `getVerificationErrorMessage` moved to @repo/common/verification-messages
// so it can source the support email from the company-info single source of
// truth without a circular @repo/common <-> @repo/db dependency.

// Failure codes Terac mints itself, for outcomes no vendor code describes
// (our own duplicate-identity block) or that a vendor reports only as prose
// (Persona/Whop rejections and review holds). They share the code space with
// StripeIdentityErrorCode above so a single breakdown of
// `User.verificationFailureCode` spans every KYC rail instead of one per
// vendor. Values are persisted, so renaming one orphans existing rows.
export const TeracIdentityFailureCode = {
  DUPLICATE_IDENTITY: "duplicate_identity",
  EXTRACTION_FAILED: "extraction_failed",
  PROVIDER_REJECTED: "provider_rejected",
  MANUAL_REVIEW_REQUIRED: "manual_review_required",
} as const;
export type TeracIdentityFailureCode =
  (typeof TeracIdentityFailureCode)[keyof typeof TeracIdentityFailureCode];

export const OptInStatus = {
  OPTED_IN: "OPTED_IN",
  OPTED_OUT: "OPTED_OUT",
} as const;
export type OptInStatus = (typeof OptInStatus)[keyof typeof OptInStatus];

export const ConfirmationStatus = {
  CONFIRMED: "CONFIRMED",
  NOT_CONFIRMED: "NOT_CONFIRMED",
} as const;
export type ConfirmationStatus =
  (typeof ConfirmationStatus)[keyof typeof ConfirmationStatus];

export const WorkLocationMatch = {
  SAME: "SAME",
  DIFFERENT: "DIFFERENT",
} as const;
export type WorkLocationMatch =
  (typeof WorkLocationMatch)[keyof typeof WorkLocationMatch];

export const PhoneType = {
  PRIMARY: "PRIMARY",
  BACKUP: "BACKUP",
} as const;
export type PhoneType = (typeof PhoneType)[keyof typeof PhoneType];

// Interview & Study
export const InterviewStatus = {
  PENDING: "PENDING",
  AWAITING_SCREENING: "AWAITING_SCREENING", // Task interview waiting for screening to pass
  ACTIVE: "ACTIVE",
  PROCESSING: "PROCESSING",
  AWAITING_REVIEW: "AWAITING_REVIEW",
  APPROVED: "APPROVED",
  REJECTED: "REJECTED",
  RETURNED: "RETURNED",
  DISPUTED: "DISPUTED",
  TIMED_OUT: "TIMED_OUT",
  FAILED: "FAILED",
  // Used when participant doesn't qualify for study and transitions to fallback
  SCREENED_OUT: "SCREENED_OUT",
  // Used when user skips an offered opportunity (wants to see another)
  SKIPPED: "SKIPPED",
  // Used when user declines all opportunities and session ends
  CANCELLED: "CANCELLED",
} as const;
export type InterviewStatus =
  (typeof InterviewStatus)[keyof typeof InterviewStatus];

// Allow-list of statuses that prove the participant actually did the interview,
// gating release of the study completion code (proof-of-completion an external
// panel accepts for payment). Deliberately an allow-list, not a deny-list of
// pre-completion states: any unlisted status withholds the code, so abandonment
// states like TIMED_OUT (set by the timeout cron on interviews that never
// joined a room) and any status added later cannot leak a payout code.
// PROCESSING is the earliest proof (the LiveKit participant_left webhook only
// sets it once the room was joined and left). SCREENED_OUT is included so a
// screened-out participant still receives the screen-out code on the finish
// screen.
export const COMPLETED_INTERVIEW_STATUSES: InterviewStatus[] = [
  InterviewStatus.PROCESSING,
  InterviewStatus.AWAITING_REVIEW,
  InterviewStatus.APPROVED,
  InterviewStatus.REJECTED,
  InterviewStatus.RETURNED,
  InterviewStatus.DISPUTED,
  InterviewStatus.FAILED,
  InterviewStatus.SCREENED_OUT,
];

// Accepts the deprecated "CONTENT_FLAGGED" value the pgEnum still carries (never
// assigned by application code, but part of the persisted column type). It is
// not in the allow-list, so it withholds the code, the safe default.
export function isInterviewCompleted(
  status: InterviewStatus | "CONTENT_FLAGGED",
): boolean {
  return COMPLETED_INTERVIEW_STATUSES.some((s) => s === status);
}

// Interview statuses where the participant won't be paid.
// Used by budget checks to exclude dead invitations from committed spend.
export const TERMINAL_NON_PAYMENT_STATUSES: InterviewStatus[] = [
  InterviewStatus.SCREENED_OUT,
  InterviewStatus.TIMED_OUT,
  InterviewStatus.RETURNED,
  InterviewStatus.REJECTED,
  InterviewStatus.FAILED,
  InterviewStatus.SKIPPED,
  InterviewStatus.CANCELLED,
];

// Purpose distinguishes screening interviews from task interviews
export const InterviewPurpose = {
  SCREENING: "SCREENING", // Screening interview before task
  TASK: "TASK", // Main task interview (may have associated screening)
  STANDALONE: "STANDALONE", // Interview without screening separation (backwards compat)
} as const;
export type InterviewPurpose =
  (typeof InterviewPurpose)[keyof typeof InterviewPurpose];

// Session status for multi-interview room sessions
export const InterviewSessionStatus = {
  ACTIVE: "ACTIVE", // Room is live, interviews ongoing
  COMPLETED: "COMPLETED", // User finished and left
  ABANDONED: "ABANDONED", // User disconnected unexpectedly
} as const;
export type InterviewSessionStatus =
  (typeof InterviewSessionStatus)[keyof typeof InterviewSessionStatus];

export const InterviewReviewDecision = {
  APPROVED: "APPROVED",
  REJECTED: "REJECTED",
} as const;
export type InterviewReviewDecision =
  (typeof InterviewReviewDecision)[keyof typeof InterviewReviewDecision];

export const InterviewRejectionCategory = {
  LOW_QUALITY: "LOW_QUALITY",
  FAILED_INSTRUCTIONS: "FAILED_INSTRUCTIONS",
  INCOMPLETE: "INCOMPLETE",
  SUSPICIOUS_PATTERNS: "SUSPICIOUS_PATTERNS",
  OTHER: "OTHER",
  STRUCK_BY_USER: "STRUCK_BY_USER",
} as const;
export type InterviewRejectionCategory =
  (typeof InterviewRejectionCategory)[keyof typeof InterviewRejectionCategory];

export const StudyStatus = {
  CREATING: "CREATING",
  DRAFT: "DRAFT",
  PRE_RECRUITING: "PRE_RECRUITING",
  ACTIVE: "ACTIVE",
  PAUSED: "PAUSED",
  FULFILLED: "FULFILLED",
  STOPPED: "STOPPED",
  COMPLETED: "COMPLETED",
  EXPIRED: "EXPIRED",
  CANCELLED: "CANCELLED",
  FAILED: "FAILED",
} as const;
export type StudyStatus = (typeof StudyStatus)[keyof typeof StudyStatus];

/**
 * Statuses in which a study is still recruiting: it appears in Browse, the
 * apply flow accepts entry (`use-cases/apply/enter-application.ts`), and
 * outreach may point people at it (`services/study/availability.ts`).
 *
 * PRE_RECRUITING publishes its listing at pre-recruitment start, which is why
 * it is live rather than a draft state. Any surface that decides whether a
 * study can be entered or referred into reads this rather than testing ACTIVE
 * alone, so the surfaces cannot disagree about which studies are reachable.
 */
export const RECRUITING_STUDY_STATUSES = [
  StudyStatus.ACTIVE,
  StudyStatus.PRE_RECRUITING,
] as const;

/**
 * Orders the researcher opportunities lists offer, shared by the API input
 * schema, the repository that builds the ORDER BY, and the pages that read
 * `?sort=` from the URL.
 */
export const STUDY_LIST_SORTS = [
  "newest",
  "oldest",
  "updated",
  "name",
] as const;
export type StudyListSort = (typeof STUDY_LIST_SORTS)[number];
export const DEFAULT_STUDY_LIST_SORT: StudyListSort = "newest";

export const StudyOrigin = {
  STANDARD: "STANDARD",
  PRE_RECRUITMENT: "PRE_RECRUITMENT",
} as const;
export type StudyOrigin = (typeof StudyOrigin)[keyof typeof StudyOrigin];

export const TERMINAL_STUDY_STATUSES: StudyStatus[] = [
  StudyStatus.COMPLETED,
  StudyStatus.STOPPED,
  StudyStatus.CANCELLED,
  StudyStatus.EXPIRED,
];

// Participant-facing "study no longer accepting in-motion submissions."
// Wider than TERMINAL_STUDY_STATUSES because it includes FULFILLED (target
// reached; block new applicants) and FAILED (which TERMINAL excludes
// because researchers may still need to review/pay outstanding work).
// From the participant's POV, all six are dead-ends.
export const PARTICIPANT_BLOCKING_STUDY_STATUSES: StudyStatus[] = [
  StudyStatus.COMPLETED,
  StudyStatus.STOPPED,
  StudyStatus.EXPIRED,
  StudyStatus.CANCELLED,
  StudyStatus.FAILED,
  StudyStatus.FULFILLED,
];

/**
 * The statuses after which an expert already on a study has nothing left to
 * do there. FULFILLED is on the blocking list only because it stops new
 * applicants: `scheduled-fulfillment-completion` holds a fulfilled study for
 * five business days so work under way can be finished, reviewed and paid.
 * PAUSED is absent from both lists for the same reason.
 */
export const PARTICIPANT_ENDED_STUDY_STATUSES: StudyStatus[] =
  PARTICIPANT_BLOCKING_STUDY_STATUSES.filter(
    (status) => status !== StudyStatus.FULFILLED,
  );

export const StudyCompletionReason = {
  BUDGET_EXHAUSTED: "BUDGET_EXHAUSTED",
  TARGET_REACHED: "TARGET_REACHED",
} as const;
export type StudyCompletionReason =
  (typeof StudyCompletionReason)[keyof typeof StudyCompletionReason];

export const RecruitmentMode = {
  AUTOMATED: "AUTOMATED",
  SELF_SOURCED: "SELF_SOURCED",
} as const;
export type RecruitmentMode =
  (typeof RecruitmentMode)[keyof typeof RecruitmentMode];

export const StudyChangelogActorType = {
  USER: "USER",
  SYSTEM: "SYSTEM",
  ADMIN: "ADMIN",
} as const;
export type StudyChangelogActorType =
  (typeof StudyChangelogActorType)[keyof typeof StudyChangelogActorType];

export const StudyChangelogEventType = {
  STATUS_CHANGE: "STATUS_CHANGE",
} as const;
export type StudyChangelogEventType =
  (typeof StudyChangelogEventType)[keyof typeof StudyChangelogEventType];

// Demo configuration (read-only demo project/study for all users)
export const DEMO_ORGANIZATION_ID = "org-demo-terac";
export const DEMO_PROJECT_ID = "proj-demo-terac";
export const DEMO_PROJECT_SLUG = "demo-research";
export const DEMO_STUDY_ID = "demo-study-terac";

export function isDemoOrganization(
  organizationId: string | null | undefined,
): boolean {
  return organizationId === DEMO_ORGANIZATION_ID;
}

export function isDemoProject(projectId: string | null | undefined): boolean {
  return projectId === DEMO_PROJECT_ID;
}

export function isDemoProjectBySlug(slug: string | null | undefined): boolean {
  return slug === DEMO_PROJECT_SLUG;
}

export function isDemoStudy(studyId: string | null | undefined): boolean {
  return studyId === DEMO_STUDY_ID;
}

export const DeviceType = {
  MOBILE_IOS: "MOBILE_IOS",
  MOBILE_ANDROID: "MOBILE_ANDROID",
  DESKTOP: "DESKTOP",
  OTHER: "OTHER",
} as const;
export type DeviceType = (typeof DeviceType)[keyof typeof DeviceType];

export const MediaRequirement = {
  NONE: "NONE",
  REQUESTED: "REQUESTED",
  REQUIRED: "REQUIRED",
} as const;
export type MediaRequirement =
  (typeof MediaRequirement)[keyof typeof MediaRequirement];

// Organization MFA requirement policy
export const MfaRequirementPolicy = {
  NOT_REQUIRED: "NOT_REQUIRED",
  REQUIRED: "REQUIRED",
} as const;
export type MfaRequirementPolicy =
  (typeof MfaRequirementPolicy)[keyof typeof MfaRequirementPolicy];

export const InterviewType = {
  STANDARD: "STANDARD", // AI-moderated (current default)
  DEMO: "DEMO",
  TEST: "TEST",
  UPLOADED: "UPLOADED", // External recording uploaded by researcher
  HUMAN_MODERATED: "HUMAN_MODERATED", // Future: Terac Meets (in-platform human interviews)
} as const;
export type InterviewType = (typeof InterviewType)[keyof typeof InterviewType];

// Referrals
export const ReferralLinkType = {
  GENERAL: "GENERAL",
  /** @deprecated AGENT-type referrals are being retired. New writes should use GENERAL or OPPORTUNITY. */
  AGENT: "AGENT",
  OPPORTUNITY: "OPPORTUNITY",
  BUSINESS: "BUSINESS",
} as const;
export type ReferralLinkType =
  (typeof ReferralLinkType)[keyof typeof ReferralLinkType];

export const ReferrerRole = {
  EXPERT: "EXPERT",
  RECRUITER: "RECRUITER",
} as const;
export type ReferrerRole = (typeof ReferrerRole)[keyof typeof ReferrerRole];

// First-touch mint surface for a ReferralLink. Set once on insert and never
// overwritten when an idempotent upsert returns the existing row. Add new
// surfaces here before threading them through from a caller.
export const ReferralLinkSource = {
  APPLY_TERMINAL: "APPLY_TERMINAL",
  OPPORTUNITY_PANEL: "OPPORTUNITY_PANEL",
  MARKETING_OPPORTUNITY: "MARKETING_OPPORTUNITY",
  MODERATOR: "MODERATOR",
  DASHBOARD: "DASHBOARD",
  POST_COMPLETION_EMAIL: "POST_COMPLETION_EMAIL",
  STUDY_REFERRAL_OUTREACH: "STUDY_REFERRAL_OUTREACH",
  ADMIN: "ADMIN",
  UNKNOWN: "UNKNOWN",
  LINQ_EXPERT_AGENT: "LINQ_EXPERT_AGENT",
  WEB_EXPERT_AGENT: "WEB_EXPERT_AGENT",
} as const;
export type ReferralLinkSource =
  (typeof ReferralLinkSource)[keyof typeof ReferralLinkSource];

// Subset of ReferralLinkSource that clients are allowed to claim. Keeps
// server-only buckets (ADMIN, MODERATOR, POST_COMPLETION_EMAIL,
// STUDY_REFERRAL_OUTREACH) out of reach of a crafted OpenAPI request so
// the funnel breakdown stays honest. The tRPC router enforces this at the
// input boundary; web hooks and other client-side callers type against
// `ClientReferralLinkSource` so the narrowing is enforced at compile time
// too.
export const CLIENT_REFERRAL_LINK_SOURCES = [
  ReferralLinkSource.APPLY_TERMINAL,
  ReferralLinkSource.OPPORTUNITY_PANEL,
  ReferralLinkSource.MARKETING_OPPORTUNITY,
  ReferralLinkSource.DASHBOARD,
  ReferralLinkSource.UNKNOWN,
] as const;
export type ClientReferralLinkSource =
  (typeof CLIENT_REFERRAL_LINK_SOURCES)[number];

export const DEFAULT_REFERRAL_COMMISSION_BPS = 2000;

/**
 * Flat lifetime commission cap stamped at signup for every (referrer,
 * referredUser) pair, regardless of link type or listing pay rate. Older
 * Referral rows stamped under the legacy `multiplier x payRate` formula keep
 * whatever value was written at their signup time, so a row's own
 * `commissionCapAmount` is what enforcement reads, unless the referrer has a
 * negotiated cap in `ReferralPartnerTerms`. New rows and public marketing
 * use this default; personalized quotes use the referrer's negotiated cap.
 */
export const REFERRAL_COMMISSION_CAP_AMOUNT = 1000;

/**
 * Default platform fee in basis points (5000 = 50%). Source of truth for both
 * the `Organization.platformFeeBps` column default and the code-level fallback
 * used when an org record is missing/partial, so the two can never drift.
 */
export const DEFAULT_PLATFORM_FEE_BPS = 5000;

// Source & Interaction
export const SourceType = {
  PLATFORM_DASHBOARD: "PLATFORM_DASHBOARD",
  PLATFORM_API: "PLATFORM_API",
  TERAC_AGENT_WEB: "TERAC_AGENT_WEB",
  TERAC_AGENT_PHONE: "TERAC_AGENT_PHONE",
  TERAC_AGENT_EMAIL: "TERAC_AGENT_EMAIL",
  INTERVIEW_CONTEXT: "INTERVIEW_CONTEXT",
  WEB_SOURCE: "WEB_SOURCE",
  SYSTEM_EVENT: "SYSTEM_EVENT",
  TRIGGER_JOB: "TRIGGER_JOB",
  UPLOAD: "UPLOAD",
  LINKEDIN_AUTH: "LINKEDIN_AUTH",
  PROLIFIC_DEMOGRAPHIC: "PROLIFIC_DEMOGRAPHIC",
} as const;
export type SourceType = (typeof SourceType)[keyof typeof SourceType];

export const InteractionType = {
  // Study Operations
  STUDY_CREATED: "STUDY_CREATED",
  STUDY_UPDATED: "STUDY_UPDATED",
  STUDY_LAUNCHED: "STUDY_LAUNCHED",
  STUDY_PAUSED: "STUDY_PAUSED",
  STUDY_RESUMED: "STUDY_RESUMED",
  STUDY_STOPPED: "STUDY_STOPPED",
  STUDY_COMPLETED: "STUDY_COMPLETED",
  STUDY_SETTINGS_UPDATED: "STUDY_SETTINGS_UPDATED",
  STUDY_BUDGET_INCREASED: "STUDY_BUDGET_INCREASED",
  STUDY_DELETED: "STUDY_DELETED",
  STUDY_INVITATION_CREATED: "STUDY_INVITATION_CREATED",
  STUDY_INVITATION_SENT: "STUDY_INVITATION_SENT",
  STUDY_INVITATION_ACCEPTED: "STUDY_INVITATION_ACCEPTED",
  STUDY_INVITATION_REJECTED: "STUDY_INVITATION_REJECTED",

  // Interview Operations
  INTERVIEW_STARTED: "INTERVIEW_STARTED",
  INTERVIEW_COMPLETED: "INTERVIEW_COMPLETED",
  INTERVIEW_APPROVED: "INTERVIEW_APPROVED",
  INTERVIEW_REJECTED: "INTERVIEW_REJECTED",
  INTERVIEW_FLAGGED: "INTERVIEW_FLAGGED",
  INTERVIEW_STATUS_CHANGED: "INTERVIEW_STATUS_CHANGED",
  INTERVIEW_UPDATED: "INTERVIEW_UPDATED",
  INTERVIEW_FAILED: "INTERVIEW_FAILED",

  // Organization Operations
  ORG_SETTINGS_UPDATED: "ORG_SETTINGS_UPDATED",
  ORG_INTEGRATION_CONNECTED: "ORG_INTEGRATION_CONNECTED",
  ORG_INTEGRATION_DISCONNECTED: "ORG_INTEGRATION_DISCONNECTED",
  ORG_MEMBER_INVITED: "ORG_MEMBER_INVITED",
  ORG_MEMBER_ADDED: "ORG_MEMBER_ADDED",
  ORG_MEMBER_ROLE_CHANGED: "ORG_MEMBER_ROLE_CHANGED",
  ORG_MEMBER_REMOVED: "ORG_MEMBER_REMOVED",
  ORG_CREDITS_ADDED: "ORG_CREDITS_ADDED",
  ORG_BILLING_UPDATED: "ORG_BILLING_UPDATED",
  ORG_WEBHOOK_CONFIGURED: "ORG_WEBHOOK_CONFIGURED",
  ORG_SSO_CONFIGURED: "ORG_SSO_CONFIGURED",

  // Project Operations
  PROJECT_CREATED: "PROJECT_CREATED",
  PROJECT_UPDATED: "PROJECT_UPDATED",
  PROJECT_DELETED: "PROJECT_DELETED",

  // Research Guide Operations
  RESEARCH_GUIDE_CREATED: "RESEARCH_GUIDE_CREATED",
  RESEARCH_GUIDE_PUBLISHED: "RESEARCH_GUIDE_PUBLISHED",
  RESEARCH_GUIDE_DELETED: "RESEARCH_GUIDE_DELETED",
  GUIDE_VERSION_CREATED: "GUIDE_VERSION_CREATED",
  GUIDE_VERSION_UPDATED: "GUIDE_VERSION_UPDATED",
  GUIDE_VERSION_DELETED: "GUIDE_VERSION_DELETED",

  // Report Operations
  REPORT_GENERATED: "REPORT_GENERATED",
  REPORT_DOWNLOADED: "REPORT_DOWNLOADED",
  REPORT_SHARED: "REPORT_SHARED",

  // User Account Management
  USER_SETTINGS_UPDATED: "USER_SETTINGS_UPDATED",
  USER_USERNAME_CHANGED: "USER_USERNAME_CHANGED",
  USER_AVATAR_UPDATED: "USER_AVATAR_UPDATED",
  USER_PHONE_NUMBER_UPDATED: "USER_PHONE_NUMBER_UPDATED",
  USER_REFERRAL_PARTNER_TERMS_UPDATED: "USER_REFERRAL_PARTNER_TERMS_UPDATED",
  USER_PRIMARY_ACCOUNT_CHANGED: "USER_PRIMARY_ACCOUNT_CHANGED",
  USER_ACCOUNT_DELETED: "USER_ACCOUNT_DELETED",
  USER_NOTIFICATION_PREFERENCES_UPDATED:
    "USER_NOTIFICATION_PREFERENCES_UPDATED",
  USER_PUSH_TOKEN_REGISTERED: "USER_PUSH_TOKEN_REGISTERED",
  USER_LEADERBOARD_PREFERENCE_UPDATED: "USER_LEADERBOARD_PREFERENCE_UPDATED",
  USER_TERMS_ACCEPTED: "USER_TERMS_ACCEPTED",
  USER_VERIFICATION_STARTED: "USER_VERIFICATION_STARTED",
  USER_VERIFICATION_SUBMITTED: "USER_VERIFICATION_SUBMITTED",
  USER_VERIFICATION_VERIFIED: "USER_VERIFICATION_VERIFIED",
  USER_VERIFICATION_FAILED: "USER_VERIFICATION_FAILED",
  USER_VERIFICATION_CANCELED: "USER_VERIFICATION_CANCELED",
  USER_PAYOUT_COMPLETED: "USER_PAYOUT_COMPLETED",
  USER_PAYOUT_REVERSED: "USER_PAYOUT_REVERSED",

  // Recruitment Operations
  RECRUITMENT_WAVE_CREATED: "RECRUITMENT_WAVE_CREATED",
  RECRUITMENT_WAVE_UPDATED: "RECRUITMENT_WAVE_UPDATED",
  RECRUITMENT_WAVE_DELETED: "RECRUITMENT_WAVE_DELETED",
  RECRUITMENT_CRITERIA_UPDATED: "RECRUITMENT_CRITERIA_UPDATED",
  RECRUITMENT_FILTER_UPDATED: "RECRUITMENT_FILTER_UPDATED",
  RECRUITMENT_ATTEMPT_LOGGED: "RECRUITMENT_ATTEMPT_LOGGED",
  RECRUITMENT_STATUS_CHANGED: "RECRUITMENT_STATUS_CHANGED",

  // Panel Management
  PANEL_CREATED: "PANEL_CREATED",
  PANEL_UPDATED: "PANEL_UPDATED",
  PANEL_DELETED: "PANEL_DELETED",
  PANEL_MEMBER_ADDED: "PANEL_MEMBER_ADDED",
  PANEL_MEMBER_REMOVED: "PANEL_MEMBER_REMOVED",
  PANEL_MEMBERS_BATCH_UPDATED: "PANEL_MEMBERS_BATCH_UPDATED",

  // Participant Management
  PARTICIPANT_SEARCHED: "PARTICIPANT_SEARCHED",
  PARTICIPANT_IMPORTED: "PARTICIPANT_IMPORTED",
  PARTICIPANT_PROFILE_UPDATED: "PARTICIPANT_PROFILE_UPDATED",
  PARTICIPANT_VERIFICATION_STATUS_CHANGED:
    "PARTICIPANT_VERIFICATION_STATUS_CHANGED",
  PARTICIPANT_APPROVAL_STATUS_CHANGED: "PARTICIPANT_APPROVAL_STATUS_CHANGED",
  PARTICIPANT_SCREENING_COMPLETED: "PARTICIPANT_SCREENING_COMPLETED",

  // Share/Access Control
  SHARE_CREATED: "SHARE_CREATED",
  SHARE_UPDATED: "SHARE_UPDATED",
  SHARE_REVOKED: "SHARE_REVOKED",
  ACCESS_LEVEL_CHANGED: "ACCESS_LEVEL_CHANGED",

  // File Management
  FILE_UPLOADED: "FILE_UPLOADED",
  FILE_DELETED: "FILE_DELETED",
  MEDIA_UPLOADED: "MEDIA_UPLOADED",
  STIMULUS_UPLOADED: "STIMULUS_UPLOADED",

  // API/Security
  API_KEY_CREATED: "API_KEY_CREATED",
  API_KEY_REVOKED: "API_KEY_REVOKED",
  API_KEY_USED: "API_KEY_USED",
  API_RATE_LIMIT_CHANGED: "API_RATE_LIMIT_CHANGED",

  // Agent Operations
  AGENT_CREATED: "AGENT_CREATED",
  AGENT_UPDATED: "AGENT_UPDATED",
  AGENT_DELETED: "AGENT_DELETED",
  AGENT_RUN_TRIGGERED: "AGENT_RUN_TRIGGERED",
  AGENT_EXECUTION_COMPLETED: "AGENT_EXECUTION_COMPLETED",
  AGENT_EXECUTION_FAILED: "AGENT_EXECUTION_FAILED",
  STUDY_EMAIL_BLAST_SENT: "STUDY_EMAIL_BLAST_SENT",
} as const;
export type InteractionType =
  (typeof InteractionType)[keyof typeof InteractionType];

export const StudyEmailAudienceMode = {
  SELECTION: "SELECTION",
  FILTER: "FILTER",
} as const;
export type StudyEmailAudienceMode =
  (typeof StudyEmailAudienceMode)[keyof typeof StudyEmailAudienceMode];

export const StudyEmailRecipientStatus = {
  QUEUED: "QUEUED",
  SENT: "SENT",
  SKIPPED: "SKIPPED",
} as const;
export type StudyEmailRecipientStatus =
  (typeof StudyEmailRecipientStatus)[keyof typeof StudyEmailRecipientStatus];

// Notifications
export const NotificationType = {
  ORGANIZATION_INVITATION_CREATED: "ORGANIZATION_INVITATION_CREATED",
  ORGANIZATION_INVITATION_ACCEPTED: "ORGANIZATION_INVITATION_ACCEPTED",
  ORGANIZATION_INVITATION_REVOKED: "ORGANIZATION_INVITATION_REVOKED",
  INTERVIEW_INVITATION_SENT: "INTERVIEW_INVITATION_SENT",
  INTERVIEW_EARNINGS_POSTED: "INTERVIEW_EARNINGS_POSTED",
  BUDGET_THRESHOLD_REACHED: "BUDGET_THRESHOLD_REACHED",
  STUDY_APPROVAL_REQUESTED: "STUDY_APPROVAL_REQUESTED",
  STUDY_APPROVAL_DECIDED: "STUDY_APPROVAL_DECIDED",
  STUDY_STARTED: "STUDY_STARTED",
  STUDY_COMPLETED: "STUDY_COMPLETED",
  INTERVIEW_APPROVED: "INTERVIEW_APPROVED",
  INTERVIEW_AUTO_APPROVED: "INTERVIEW_AUTO_APPROVED",
  INTERVIEW_REJECTED: "INTERVIEW_REJECTED",
  PAYMENT_CLAWED_BACK: "PAYMENT_CLAWED_BACK",
  BALANCE_NEGATIVE: "BALANCE_NEGATIVE",
  INVITATION_REMINDER: "INVITATION_REMINDER",
  INVITATION_REOPENED: "INVITATION_REOPENED",
  POST_FIRST_INTERVIEW: "POST_FIRST_INTERVIEW",
  INVITATION_FEEDBACK_REQUEST: "INVITATION_FEEDBACK_REQUEST",
  POWER_USER_NPS: "POWER_USER_NPS",
  STUDY_STUCK: "STUDY_STUCK",
  STUDY_FULFILLMENT_ALERT: "STUDY_FULFILLMENT_ALERT",
  SUPPORT_AUTO_RESPONSE: "SUPPORT_AUTO_RESPONSE",
  WELCOME: "WELCOME",
  REFERRAL_OUTREACH_REQUEST: "REFERRAL_OUTREACH_REQUEST",
  POST_COMPLETION_REFERRAL: "POST_COMPLETION_REFERRAL",
  NEXT_TASK_AVAILABLE: "NEXT_TASK_AVAILABLE",
  TASK_APPROVED: "TASK_APPROVED",
  // Lifecycle emails
  WELCOME_NUDGE_FIRST: "WELCOME_NUDGE_FIRST",
  WELCOME_NUDGE_LAST: "WELCOME_NUDGE_LAST",
  FINISH_PROFILE_FIRST: "FINISH_PROFILE_FIRST",
  FINISH_PROFILE_LAST: "FINISH_PROFILE_LAST",
  ONBOARDING_CONFIRMED: "ONBOARDING_CONFIRMED",
  APPROVED_NOT_STARTED_FIRST: "APPROVED_NOT_STARTED_FIRST",
  APPROVED_NOT_STARTED_LAST: "APPROVED_NOT_STARTED_LAST",
  APPLIED_SCREENED_OUT: "APPLIED_SCREENED_OUT",
  APPLIED_NOT_SELECTED: "APPLIED_NOT_SELECTED",
  DORMANT_30_DAYS: "DORMANT_30_DAYS",
  DORMANT_60_DAYS: "DORMANT_60_DAYS",
  DORMANT_90_DAYS: "DORMANT_90_DAYS",
  COMPLETE_MATCH_PROFILE: "COMPLETE_MATCH_PROFILE",
  STUDY_ETA_SET: "STUDY_ETA_SET",
  STUDY_ETA_UPDATED: "STUDY_ETA_UPDATED",
  // Referrer alert: a new opportunity listing is available to refer
  OPPORTUNITY_LISTED: "OPPORTUNITY_LISTED",
  // First-opportunity onboarding emails (screening review wait + screen pass)
  FIRST_OPPORTUNITY_AWAITING_REVIEW: "FIRST_OPPORTUNITY_AWAITING_REVIEW",
  FIRST_OPPORTUNITY_SCREEN_PASSED: "FIRST_OPPORTUNITY_SCREEN_PASSED",
  // Screen-pass approval for a returning expert. Split from the first-opportunity
  // type so activation copy stays aimed at newcomers and the two cohorts can be
  // measured apart.
  OPPORTUNITY_SCREEN_PASSED: "OPPORTUNITY_SCREEN_PASSED",
  // A screening step the applicant can start opened up while they had nothing
  // to do: a reviewer cleared what was blocking it, or a live plan edit gave
  // them one. The submission stays in SCREENING, so nothing else announces it.
  SCREENING_STEP_READY: "SCREENING_STEP_READY",
  // Network connections: "your connection {name} fits study X" digest
  CONNECTION_MATCH: "CONNECTION_MATCH",
  // Feasibility: the researcher's study-creation quote is priced and ready to review
  FEASIBILITY_QUOTE_READY: "FEASIBILITY_QUOTE_READY",
  // Researcher bump: experts shared their calendars and are waiting to be booked
  STUDY_BOOKING_NEEDED: "STUDY_BOOKING_NEEDED",
  // Researcher digest: applicant and completed-work decisions awaiting review
  RESEARCHER_REVIEW_DIGEST: "RESEARCHER_REVIEW_DIGEST",
  // Terac-booked scheduling: a slot was taken on the expert's calendar. Google
  // is given no attendees on that event, so this is the only thing that tells
  // either side the call exists.
  INTERVIEW_BOOKED: "INTERVIEW_BOOKED",
  // Terac-booked scheduling: the expert's nudge on the day of the call, and
  // again an hour before it. The stage lives in the payload, not in the type.
  INTERVIEW_REMINDER: "INTERVIEW_REMINDER",
  // Ad-hoc message an SPL sent to selected participants of one study
  STUDY_EMAIL_BLAST: "STUDY_EMAIL_BLAST",
  // The expert's copy of a contract they signed through an E_SIGNATURE task
  CONTRACT_SIGNED: "CONTRACT_SIGNED",
  OFFICE_HOURS_ANNOUNCED: "OFFICE_HOURS_ANNOUNCED",
  // An expert rejected off a project study they were on
  PROJECT_OFFBOARDED: "PROJECT_OFFBOARDED",
  // Everyone still on a project study when it closes
  PROJECT_CLOSED: "PROJECT_CLOSED",
} as const;
export type NotificationType =
  (typeof NotificationType)[keyof typeof NotificationType];

export const NotificationReadStatus = {
  UNREAD: "UNREAD",
  READ: "READ",
  SEEN: "SEEN",
} as const;
export type NotificationReadStatus =
  (typeof NotificationReadStatus)[keyof typeof NotificationReadStatus];

export const NotificationChannel = {
  EMAIL: "EMAIL",
  IN_APP: "IN_APP",
  PUSH: "PUSH",
  PHONE_CALL: "PHONE_CALL",
} as const;
export type NotificationChannel =
  (typeof NotificationChannel)[keyof typeof NotificationChannel];

export const InteractionSource = {
  EMAIL_LINK: "EMAIL_LINK",
  SMS_LINK: "SMS_LINK",
  PUSH_NOTIFICATION: "PUSH_NOTIFICATION",
  WEB_FEED: "WEB_FEED",
  IOS_FEED: "IOS_FEED",
  ANDROID_FEED: "ANDROID_FEED",
  WEB_NOTIFICATIONS: "WEB_NOTIFICATIONS",
  IOS_NOTIFICATIONS: "IOS_NOTIFICATIONS",
  PROLIFIC: "PROLIFIC",
  USER_INTERVIEWS: "USER_INTERVIEWS",
  MAVEN: "MAVEN",
  RESPONDENT: "RESPONDENT",
  EMPORIA: "EMPORIA",
  NEWTON: "NEWTON",
  GOOGLE_ADS: "GOOGLE_ADS",
  FACEBOOK_ADS: "FACEBOOK_ADS",
  LINKEDIN: "LINKEDIN",
  TWITTER: "TWITTER",
  REDDIT: "REDDIT",
  FACEBOOK: "FACEBOOK",
  PUBLIC_LINK: "PUBLIC_LINK",
  DIRECT_URL: "DIRECT_URL",
  UNKNOWN: "UNKNOWN",
  SESSION_TRANSITION: "SESSION_TRANSITION",
} as const;
export type InteractionSource =
  (typeof InteractionSource)[keyof typeof InteractionSource];

export const NotificationState = {
  QUEUED: "QUEUED",
  SENT: "SENT",
  DELIVERED: "DELIVERED",
  SEEN: "SEEN",
  CLICKED: "CLICKED",
  FAILED: "FAILED",
  THROTTLED: "THROTTLED",
  BLOCKED: "BLOCKED",
  BOUNCED: "BOUNCED",
  UNSUBSCRIBED: "UNSUBSCRIBED",
} as const;
export type NotificationState =
  (typeof NotificationState)[keyof typeof NotificationState];

export const NotificationDeliveryStatus = {
  QUEUED: "QUEUED",
  IN_PROGRESS: "IN_PROGRESS",
  DELIVERED: "DELIVERED",
  FAILED: "FAILED",
} as const;
export type NotificationDeliveryStatus =
  (typeof NotificationDeliveryStatus)[keyof typeof NotificationDeliveryStatus];

export const ArchivalStatus = {
  ACTIVE: "ACTIVE",
  ARCHIVED: "ARCHIVED",
  SOFT_DELETED: "SOFT_DELETED",
} as const;
export type ArchivalStatus =
  (typeof ArchivalStatus)[keyof typeof ArchivalStatus];

// Organization invitations (Better Auth, lowercase values)
export const OrgInvitationStatus = {
  PENDING: "pending",
  ACCEPTED: "accepted",
  DECLINED: "declined",
  CANCELED: "canceled",
  REJECTED: "rejected",
} as const;
export type OrgInvitationStatus =
  (typeof OrgInvitationStatus)[keyof typeof OrgInvitationStatus];

// Study invitations
export const InvitationStatus = {
  PENDING: "PENDING",
  ACTIVE: "ACTIVE",
  VIEWED: "VIEWED",
  ACCEPTED: "ACCEPTED",
  DECLINED: "DECLINED",
  EXPIRED: "EXPIRED",
  RESCINDED: "RESCINDED",
  COMPLETED: "COMPLETED",
} as const;
export type InvitationStatus =
  (typeof InvitationStatus)[keyof typeof InvitationStatus];

// Outcome of an entry attempt on the apply flow. `engagedAt` and
// `entryOutcome` go together: both null = pre-engagement (push invitation
// waiting for user), both set = user engaged with this outcome.
export const EntryOutcome = {
  CREATED: "CREATED",
  RESUMED: "RESUMED",
  NOT_ELIGIBLE: "NOT_ELIGIBLE",
  LINK_EXPIRED: "LINK_EXPIRED",
  LINK_REVOKED: "LINK_REVOKED",
  LINK_PAUSED: "LINK_PAUSED",
  LINK_COMPLETED: "LINK_COMPLETED",
  STUDY_UNAVAILABLE: "STUDY_UNAVAILABLE",
  CAPACITY_REACHED: "CAPACITY_REACHED",
  /**
   * @deprecated Not surfaced anymore. The apply flow routes every existing
   * submission to its submission page regardless of state (APPROVED, REJECTED,
   * etc.); the submission's own rendering handles the terminal UX. Kept in
   * the enum for historical rows written before the redesign.
   */
  DUPLICATE_COMPLETED: "DUPLICATE_COMPLETED",
  DEVICE_NOT_ALLOWED: "DEVICE_NOT_ALLOWED",
} as const;
export type EntryOutcome = (typeof EntryOutcome)[keyof typeof EntryOutcome];

// Whether each entryOutcome hides the invitation from the participant feed
// (/me/opportunities). Surfacing a row whose apply gate already rejected the
// user is what produced the phantom-invitation bug (ENG-600).
//
// `satisfies Record<EntryOutcome, boolean>` forces every future enum value to
// declare its intent here; adding `FRAUD_BLOCKED` (or anything else) to
// EntryOutcome without classifying it is a type error, so the read-side
// filter can't silently drift from the write-side definition again.
//
// Hidden (true) = apply gate blocked the user AND the block is permanent
//   for this invitation: re-clicking produces the same result.
// Shown (false) = either the user can still act (pre-engagement push,
//   submission opened, or recoverable like DEVICE_NOT_ALLOWED where
//   switching device makes it actionable again).
const ENTRY_OUTCOME_HIDES_FROM_FEED = {
  CREATED: false,
  RESUMED: false,
  DEVICE_NOT_ALLOWED: false,
  NOT_ELIGIBLE: true,
  LINK_EXPIRED: true,
  LINK_REVOKED: true,
  LINK_PAUSED: true,
  LINK_COMPLETED: true,
  STUDY_UNAVAILABLE: true,
  CAPACITY_REACHED: true,
  DUPLICATE_COMPLETED: true,
} as const satisfies Record<EntryOutcome, boolean>;

export const FEED_HIDDEN_ENTRY_OUTCOMES = (
  Object.keys(ENTRY_OUTCOME_HIDES_FROM_FEED) as EntryOutcome[]
).filter((o) => ENTRY_OUTCOME_HIDES_FROM_FEED[o]);

export const InvitationOrigin = {
  MATCHED: "MATCHED",
  MANUAL: "MANUAL",
  PUBLIC_LINK: "PUBLIC_LINK",
  INTERNAL_STUDY: "INTERNAL_STUDY",
  INTERNAL_PANEL: "INTERNAL_PANEL",
  INTERNAL_INFERRED: "INTERNAL_INFERRED",
  EXTERNAL_PANEL: "EXTERNAL_PANEL",
  SOCIAL_POST: "SOCIAL_POST",
  COLD_OUTREACH: "COLD_OUTREACH",
  REFERRAL: "REFERRAL",
} as const;
export type InvitationOrigin =
  (typeof InvitationOrigin)[keyof typeof InvitationOrigin];

// Recruitment Workflow
export const WorkflowStatus = {
  ACTIVE: "ACTIVE",
  PAUSED: "PAUSED",
  STOPPED: "STOPPED",
  FAILED: "FAILED",
  COMPLETED: "COMPLETED",
} as const;
export type WorkflowStatus =
  (typeof WorkflowStatus)[keyof typeof WorkflowStatus];

export const ChannelRunStatus = {
  LAUNCHING: "LAUNCHING",
  AWAITING_REVIEW: "AWAITING_REVIEW",
  ACTIVE: "ACTIVE",
  PAUSED: "PAUSED",
  STOPPED: "STOPPED",
  FAILED: "FAILED",
} as const;
export type ChannelRunStatus =
  (typeof ChannelRunStatus)[keyof typeof ChannelRunStatus];

export const RecruitmentChannelType = {
  INTERNAL_PANEL: "INTERNAL_PANEL",
  USER_INTERVIEWS: "USER_INTERVIEWS",
  RESPONDENT: "RESPONDENT",
  OUTBOUND: "OUTBOUND",
  JOB_BOARDS: "JOB_BOARDS",
  ADS: "ADS",
  // Its own type rather than a second ADS run: ADS is Meta-only end to end
  // (adapter, launch task, creative platform), and a shared type would give
  // two live campaigns on two networks one status and one set of controls.
  LINKEDIN_ADS: "LINKEDIN_ADS",
  SORCE: "SORCE",
  // Recruits from the internally-bought lead pool (Apollo, Clay). Parks its run
  // at AWAITING_REVIEW holding one pending audience per study; the study's
  // Leads tab is where that audience is grown and approved.
  INTERNAL_LEADS: "INTERNAL_LEADS",
} as const;
export type RecruitmentChannelType =
  (typeof RecruitmentChannelType)[keyof typeof RecruitmentChannelType];

export const WorkflowActivityType = {
  WORKFLOW_STARTED: "WORKFLOW_STARTED",
  WORKFLOW_PAUSED: "WORKFLOW_PAUSED",
  WORKFLOW_RESUMED: "WORKFLOW_RESUMED",
  WORKFLOW_STOPPED: "WORKFLOW_STOPPED",
  WORKFLOW_COMPLETED: "WORKFLOW_COMPLETED",
  CHANNEL_CREATED: "CHANNEL_CREATED",
  CHANNEL_LAUNCHED: "CHANNEL_LAUNCHED",
  CHANNEL_PAUSED: "CHANNEL_PAUSED",
  CHANNEL_STOPPED: "CHANNEL_STOPPED",
  CHANNEL_FAILED: "CHANNEL_FAILED",
  CHANNEL_SELECTED: "CHANNEL_SELECTED",
  ANOMALY_DETECTED: "ANOMALY_DETECTED",
  SUBMISSION_APPROVED: "SUBMISSION_APPROVED",
  SUBMISSION_REJECTED: "SUBMISSION_REJECTED",
  PROGRESS_CHECKED: "PROGRESS_CHECKED",
  STUDY_LAUNCHED: "STUDY_LAUNCHED",
  STUDY_PAUSED: "STUDY_PAUSED",
  STUDY_RESUMED: "STUDY_RESUMED",
  STUDY_STOPPED: "STUDY_STOPPED",
  STUDY_COMPLETED: "STUDY_COMPLETED",
  STUDY_FULFILLED: "STUDY_FULFILLED",
} as const;
export type WorkflowActivityType =
  (typeof WorkflowActivityType)[keyof typeof WorkflowActivityType];

// Screening requirement for combined interview flow
export const ScreeningRequirement = {
  REQUIRED: "REQUIRED",
  SKIPPED: "SKIPPED",
  NOT_APPLICABLE: "NOT_APPLICABLE",
} as const;
export type ScreeningRequirement =
  (typeof ScreeningRequirement)[keyof typeof ScreeningRequirement];

// Screening outcome from real-time evaluation during interview
export const ScreeningOutcome = {
  PASSED: "PASSED",
  FAILED: "FAILED",
  NOT_EVALUATED: "NOT_EVALUATED",
} as const;
export type ScreeningOutcome =
  (typeof ScreeningOutcome)[keyof typeof ScreeningOutcome];

// Fallback interview type when screening fails
export const FallbackInterviewType = {
  ONBOARDING: "ONBOARDING",
  INTERNAL_STUDY: "INTERNAL_STUDY",
} as const;
export type FallbackInterviewType =
  (typeof FallbackInterviewType)[keyof typeof FallbackInterviewType];

/**
 * Platform-specific overbooking factors for recruitment
 *
 * Overbooking factor determines how many invitations to send relative to remaining budget.
 * Higher factor = more aggressive overbooking (more invitations than can be paid for)
 *
 * Rationale:
 * - Reddit: Very low conversion rates (1-5%), need massive overbooking
 * - User Interviews: Professional platform, 1:1 booking
 * - Internal Panel: Known audience, moderate overbooking
 */
export const PLATFORM_OVERBOOKING_FACTORS = {
  // Reddit and social posts - extremely low conversion
  SOCIAL_POST: 20,
  REDDIT: 20,

  // External panels - precise
  EXTERNAL_PANEL: 1,
  USER_INTERVIEWS: 1,

  // Internal sources - moderate
  INTERNAL_PANEL: 5,
  INTERNAL_INFERRED: 3,

  // Default for unknown sources
  DEFAULT: 2,
} as const;

export type PlatformOverbookingKey = keyof typeof PLATFORM_OVERBOOKING_FACTORS;

/**
 * Get overbooking factor for a given invitation origin
 */
export function getOverbookingFactor(
  origin: string,
  platform?: string,
): number {
  // Platform-specific overrides
  if (platform?.toLowerCase().includes("reddit")) {
    return PLATFORM_OVERBOOKING_FACTORS.REDDIT;
  }
  // Origin-based lookup
  switch (origin) {
    case InvitationOrigin.SOCIAL_POST:
      return PLATFORM_OVERBOOKING_FACTORS.SOCIAL_POST;
    case InvitationOrigin.EXTERNAL_PANEL:
      return PLATFORM_OVERBOOKING_FACTORS.EXTERNAL_PANEL;
    case InvitationOrigin.INTERNAL_PANEL:
      return PLATFORM_OVERBOOKING_FACTORS.INTERNAL_PANEL;
    case InvitationOrigin.INTERNAL_INFERRED:
      return PLATFORM_OVERBOOKING_FACTORS.INTERNAL_INFERRED;
    default:
      return PLATFORM_OVERBOOKING_FACTORS.DEFAULT;
  }
}

// Internal platform roles
export const InternalRole = {
  SUPERADMIN: "SUPERADMIN",
  SPL: "SPL",
  RECRUITER: "RECRUITER",
  SUPPORT: "SUPPORT",
  // Reads the ledger and nothing else. Exists for the people who reconcile the
  // money without running any part of the product, which includes accountants
  // outside the company.
  FINANCE: "FINANCE",
} as const;
export type InternalRole = (typeof InternalRole)[keyof typeof InternalRole];

/**
 * Internal roles that are staff but must not inherit the blanket internal
 * privileges that predate the role system. A restricted role reaches only what
 * its `InternalScope` grant allows, and nothing else.
 *
 * Membership here is what excludes a role from every gate that reads
 * `ctx.isSuperAdmin`: the `teracTeamProcedure` surface, the
 * `userType: "INTERNAL"` authorization arm, and the researcher super-admin
 * bypass. None of those consult scopes at all, so a role left out of this list
 * holds far more than its `INTERNAL_ROLE_SCOPES` entry claims. Any new role
 * belongs here unless it is genuinely meant to run the whole platform.
 * `internal-role-scopes.test.ts` pins the list so a change has to be deliberate.
 *
 * The two members are restricted for different reasons, which is why this is
 * not the same thing as being study-scoped: RECRUITER is bounded to its
 * `StudyRecruiter` assignments, while FINANCE is bounded by holding a single
 * read scope and no study access whatsoever.
 */
export const RESTRICTED_INTERNAL_ROLES: readonly InternalRole[] = [
  InternalRole.RECRUITER,
  InternalRole.FINANCE,
];

export function isRestrictedInternalRole(
  role: InternalRole | null | undefined,
): boolean {
  return role != null && RESTRICTED_INTERNAL_ROLES.includes(role);
}

/**
 * Whether the principal is Terac staff at all. Holding an `internalRole` is the
 * whole test: a verified @terac.com address grants nothing on its own, so a
 * staff member nobody has given a role to sees exactly what a stranger sees,
 * and a contractor on a personal address can be admitted as a RECRUITER.
 */
export function isTeracStaff(role: InternalRole | null | undefined): boolean {
  return role != null;
}

/**
 * Whether the principal carries the broad internal authority that the
 * `@terac.com` domain used to confer by itself.
 */
export function hasUnrestrictedInternalAccess(
  role: InternalRole | null | undefined,
): boolean {
  return isTeracStaff(role) && !isRestrictedInternalRole(role);
}

/**
 * Superadmin "view as" impersonation targets. Extends InternalRole with
 * CUSTOMER, which is a UI preview (client-side isSuperAdmin override +
 * study-route guard), not a server-side permission downgrade.
 */
export const ViewAs = {
  ...InternalRole,
  CUSTOMER: "CUSTOMER",
} as const;
export type ViewAs = (typeof ViewAs)[keyof typeof ViewAs];

// Single source of truth for the impersonation state, readable by both the
// web client (document.cookie) and the API context (request headers).
// Absence of the cookie means SUPERADMIN (no impersonation).
export const VIEW_AS_COOKIE_NAME = "terac-view-as";

export function parseViewAs(value: string | null | undefined): ViewAs {
  if (value && Object.values(ViewAs).includes(value as ViewAs)) {
    return value as ViewAs;
  }
  return ViewAs.SUPERADMIN;
}

/**
 * Who each real role may impersonate via the terac-view-as cookie. Every target
 * must hold a subset of the impersonator's scopes, which is what makes the
 * cookie narrowing-only. A role missing from this map impersonates nobody, and
 * no role can impersonate itself.
 *
 * RECRUITER holds `studiesAccess` and nothing else, and an SPL already has it,
 * so an SPL stepping down to RECRUITER only loses reach.
 */
const IMPERSONATION_TARGETS: Partial<
  Record<InternalRole, readonly InternalRole[]>
> = {
  [InternalRole.SUPERADMIN]: [
    InternalRole.SPL,
    InternalRole.RECRUITER,
    InternalRole.SUPPORT,
    InternalRole.FINANCE,
  ],
  [InternalRole.SPL]: [InternalRole.RECRUITER],
};

/**
 * Resolves the effective internal role for permission checks. `null` means "no
 * internal role"; i.e. resolve scopes as a plain customer would have them
 * (none). Two rules, both narrowing-only so a cookie can never escalate:
 *   - override === CUSTOMER -> null, for ANY real internal role. This is what
 *     makes a "view as customer" preview faithful: every internal scope
 *     disappears, so scope-gated affordances (batch review, screening edits,
 *     internal-only actions) match exactly what a real customer sees and can
 *     do. Not a UI-only flip; it applies server-side too.
 *   - override === another internal role -> that role, but only when
 *     `IMPERSONATION_TARGETS` lets the caller's real role reach it. Every other
 *     pairing ignores the override and keeps the real role.
 *
 * Lives here rather than beside its API call sites because the client-side
 * `isSuperAdmin` needs it too, and `@repo/api` cannot be imported from a client
 * bundle.
 */
export function resolveEffectiveInternalRole(
  realRole: InternalRole,
  override: string | undefined,
): InternalRole | null {
  if (override === ViewAs.CUSTOMER) {
    return null;
  }
  if (!override) {
    return realRole;
  }
  const targets: readonly string[] = IMPERSONATION_TARGETS[realRole] ?? [];
  if (!targets.includes(override)) {
    return realRole;
  }
  return override as InternalRole;
}

// Sentinel for "no SPL assigned" in the internal organizations SPL filter.
// Owner ids are cuids, so this can never collide with a real user id.
export const UNASSIGNED_SPL_FILTER_VALUE = "unassigned";

// Sortable columns on the internal organizations table. Shared so the API can
// validate the requested sort against exactly what the query can order by.
export const INTERNAL_ORGANIZATION_SORT_FIELDS = [
  "name",
  "members",
  "projects",
  "spl",
  "totalSpent",
  "createdAt",
] as const;
export type InternalOrganizationSortField =
  (typeof INTERNAL_ORGANIZATION_SORT_FIELDS)[number];

// Sortable columns on the internal users table, validated by the API the same way.
export const INTERNAL_USER_SORT_FIELDS = ["name", "createdAt"] as const;
export type InternalUserSortField = (typeof INTERNAL_USER_SORT_FIELDS)[number];

// Internal platform scopes
export const InternalScope = {
  studiesAccess: "studiesAccess",
  internalPlatformAccess: "internalPlatformAccess",
  customerPlatformAccess: "customerPlatformAccess",
  editStudySpl: "editStudySpl",
  editStudyEngineer: "editStudyEngineer",
  assignStudyRecruiters: "assignStudyRecruiters",
  editStudyRecruitment: "editStudyRecruitment",
  editStudySettings: "editStudySettings",
  editStudyChannels: "editStudyChannels",
  reviewSubmissions: "reviewSubmissions",
  batchReviewSubmissions: "batchReviewSubmissions",
  skipSubmissionTasks: "skipSubmissionTasks",
  kpisAccess: "kpisAccess",
  viewAdmin: "viewAdmin",
  editAdmin: "editAdmin",
  editScreeningQuestions: "editScreeningQuestions",
  manageOrgCredits: "manageOrgCredits",
  manageOrganizations: "manageOrganizations",
  // Opening a new client organization only. Everything after that -- members,
  // invitations, email domains -- stays behind manageOrganizations.
  createOrganizations: "createOrganizations",
  manageExpertCredits: "manageExpertCredits",
  managePayouts: "managePayouts",
  manageManualPayouts: "manageManualPayouts",
  payStoppedStudy: "payStoppedStudy",
  // Pricing a payout row below cost spends Terac's own money. Its own scope
  // rather than `manageExpertCredits`, which also opens /internal/add-credits:
  // an uncapped, study-less gift to any account. This one is bounded by the
  // subsidy caps and by the study the row belongs to.
  payBelowCost: "payBelowCost",
  // Releasing a manual payout batch to the ledger. Separate from
  // `manageManualPayouts`, which only builds the batch: SPL and SUPPORT price
  // the rows, and a superadmin decides whether that money leaves. Granting this
  // to a role that already holds `manageManualPayouts` removes the gate for
  // that role, so it stays SUPERADMIN-only.
  approvePayouts: "approvePayouts",
  manageFeasibility: "manageFeasibility",
  runSplAgent: "runSplAgent",
  manageReferrals: "manageReferrals",
  editUserProfile: "editUserProfile",
  viewUserProfile: "viewUserProfile",
  manageTestSubmissions: "manageTestSubmissions",
  sendStudyEmails: "sendStudyEmails",
  sendExpertTextMessages: "sendExpertTextMessages",
  preQualifyParticipants: "preQualifyParticipants",
  // The expert rate board. SUPERADMIN and SPL only: a rate set there becomes
  // live money once payouts read the table, including for work already done.
  manageExpertRates: "manageExpertRates",
  // Read-only reach over the whole double-entry ledger: every account, every
  // transaction, every entry, for every customer and expert on the platform.
  // Nothing here writes, but the money history of the entire business is a
  // wider read than any one job needs, so it stays with SUPERADMIN.
  viewLedger: "viewLedger",
  // Hand-entered time written into the tracker. An entry there can be neither
  // edited nor deleted and pays in full, so it stays with SUPERADMIN and SPL,
  // and an SPL only on the opportunities they own (`canAddHandEnteredTime`).
  addHandEnteredTime: "addHandEnteredTime",
} as const;
export type InternalScope = (typeof InternalScope)[keyof typeof InternalScope];

// Maps each internal role to its granted scopes
export const INTERNAL_ROLE_SCOPES: Record<
  InternalRole,
  readonly InternalScope[]
> = {
  SUPERADMIN: [
    InternalScope.studiesAccess,
    InternalScope.internalPlatformAccess,
    InternalScope.customerPlatformAccess,
    InternalScope.editStudySpl,
    InternalScope.editStudyEngineer,
    InternalScope.assignStudyRecruiters,
    InternalScope.editStudyRecruitment,
    InternalScope.editStudySettings,
    InternalScope.editStudyChannels,
    InternalScope.reviewSubmissions,
    InternalScope.batchReviewSubmissions,
    InternalScope.skipSubmissionTasks,
    InternalScope.kpisAccess,
    InternalScope.viewAdmin,
    InternalScope.editAdmin,
    InternalScope.editScreeningQuestions,
    InternalScope.manageOrgCredits,
    InternalScope.manageOrganizations,
    InternalScope.createOrganizations,
    InternalScope.manageExpertCredits,
    InternalScope.managePayouts,
    InternalScope.manageManualPayouts,
    InternalScope.payStoppedStudy,
    InternalScope.payBelowCost,
    InternalScope.approvePayouts,
    InternalScope.manageFeasibility,
    InternalScope.manageReferrals,
    InternalScope.editUserProfile,
    InternalScope.viewUserProfile,
    InternalScope.manageTestSubmissions,
    InternalScope.sendStudyEmails,
    InternalScope.sendExpertTextMessages,
    InternalScope.preQualifyParticipants,
    InternalScope.runSplAgent,
    InternalScope.manageExpertRates,
    InternalScope.viewLedger,
    InternalScope.addHandEnteredTime,
  ],
  SPL: [
    InternalScope.studiesAccess,
    InternalScope.createOrganizations,
    InternalScope.assignStudyRecruiters,
    InternalScope.editStudyRecruitment,
    InternalScope.editStudySettings,
    InternalScope.editStudyChannels,
    InternalScope.editScreeningQuestions,
    InternalScope.skipSubmissionTasks,
    InternalScope.manageOrganizations,
    InternalScope.manageManualPayouts,
    InternalScope.payStoppedStudy,
    InternalScope.payBelowCost,
    InternalScope.manageFeasibility,
    InternalScope.viewUserProfile,
    InternalScope.manageTestSubmissions,
    InternalScope.sendStudyEmails,
    InternalScope.sendExpertTextMessages,
    InternalScope.runSplAgent,
    InternalScope.manageExpertRates,
    InternalScope.addHandEnteredTime,
  ],
  // Deliberately the smallest grant of any role. A recruiter is a contract
  // caller working a named list of studies, and studiesAccess is bounded to
  // those by their StudyRecruiter rows. viewUserProfile is absent because it
  // has no such bound: it reaches any account on the platform, which is a
  // wider reach than the job needs. Contact details come from the invited list
  // of a study they are actually on.
  RECRUITER: [InternalScope.studiesAccess],
  SUPPORT: [
    InternalScope.studiesAccess,
    InternalScope.manageOrganizations,
    InternalScope.manageExpertCredits,
    InternalScope.manageManualPayouts,
    InternalScope.payStoppedStudy,
    InternalScope.payBelowCost,
    InternalScope.editUserProfile,
    InternalScope.viewUserProfile,
    // Deliberately granted, reversing the exclusion #9374 enforced. That PR was
    // right that the phone branch needs a scope of its own and right that
    // nobody had ever argued SUPPORT should hold it; what it inferred from the
    // silence was that the answer was no. The answer is yes. Support answers
    // experts, and an expert who wrote in by text is still an expert waiting.
    // The gate itself stands: it is what keeps the roles that should not text
    // from texting, and it is still the only thing standing between a role and
    // a paid send on a shared line.
    InternalScope.sendExpertTextMessages,
  ],
  // Reads the ledger, and that is the entire grant. No study access, no
  // customer or expert administration, and nothing that writes. The ledger is
  // already the whole company's money, so anything added here widens what an
  // outside accountant sees rather than making the job possible.
  FINANCE: [InternalScope.internalPlatformAccess, InternalScope.viewLedger],
} as const;

export const InternalStudyStatus = {
  PENDING: "PENDING",
  ACTIVE: "ACTIVE",
  COMPLETED: "COMPLETED",
  ANALYZED: "ANALYZED",
  CANCELLED: "CANCELLED",
} as const;
export type InternalStudyStatus =
  (typeof InternalStudyStatus)[keyof typeof InternalStudyStatus];

export const InternalStudyType = {
  MEMORY_BASED: "MEMORY_BASED",
  INSIGHT_EXPLOIT: "INSIGHT_EXPLOIT",
  INSIGHT_EXPLORE: "INSIGHT_EXPLORE",
  ONBOARDING: "ONBOARDING",
  REFERRAL_SCREENING: "REFERRAL_SCREENING",
  NEWS_BASED: "NEWS_BASED",
  TRENDING_NEWS: "TRENDING_NEWS",
} as const;
export type InternalStudyType =
  (typeof InternalStudyType)[keyof typeof InternalStudyType];

export const StudyType = {
  REGULAR: "REGULAR",
  SCREENING: "SCREENING",
} as const;
export type StudyType = (typeof StudyType)[keyof typeof StudyType];

export const StudyPayRateFrequency = {
  ONE_TIME: "ONE_TIME",
  HOURLY: "HOURLY",
  PER_TASK: "PER_TASK",
} as const;
export type StudyPayRateFrequency =
  (typeof StudyPayRateFrequency)[keyof typeof StudyPayRateFrequency];

/** The two project-based frequencies, for copy that has to name the right one. */
export type ProjectBasedPayRate = Extract<
  StudyPayRateFrequency,
  "HOURLY" | "PER_TASK"
>;

/**
 * Ongoing work paid per hour or per approved task, rather than a fixed amount
 * per submission. Listed explicitly, not as `!== ONE_TIME`, so a value added
 * later reads as NOT project-based: the side where fees get charged.
 */
export const PROJECT_BASED_PAY_RATES: ProjectBasedPayRate[] = [
  StudyPayRateFrequency.HOURLY,
  StudyPayRateFrequency.PER_TASK,
];

export function isProjectBasedPayRate(
  payRateFrequency?: StudyPayRateFrequency | null,
): payRateFrequency is ProjectBasedPayRate {
  return PROJECT_BASED_PAY_RATES.some((rate) => rate === payRateFrequency);
}

// A delivery turnaround only means something for a one-time engagement, where a
// fixed number of interviews is delivered by a date. Hourly and per-task
// engagements are open-ended, so no turnaround is tracked, nagged about, or
// displayed for them.
export function studyHasTurnaround(study: {
  payRateFrequency?: StudyPayRateFrequency | null;
}): boolean {
  return !isProjectBasedPayRate(study.payRateFrequency);
}

export const OpportunityListingCategory = {
  PROJECT_BASED: "PROJECT_BASED",
  ONE_TIME: "ONE_TIME",
} as const;
export type OpportunityListingCategory =
  (typeof OpportunityListingCategory)[keyof typeof OpportunityListingCategory];

/**
 * How experts see a study grouped. Always derived from the pay unit, never
 * stored: the listing's own `category` column was never kept in step with it.
 */
export function listingCategoryForPayRate(
  payRateFrequency?: StudyPayRateFrequency | null,
): OpportunityListingCategory {
  return isProjectBasedPayRate(payRateFrequency)
    ? OpportunityListingCategory.PROJECT_BASED
    : OpportunityListingCategory.ONE_TIME;
}

/**
 * The units a payout line is offered by default.
 *
 * Not a closed set: PayoutBatch.unit / PayoutBatchItem.unit stay free text and
 * the pay screen can write a custom unit, because the work Terac bills is not
 * only hours and tasks. These three are the shortlist, stored as the singular
 * lowercase noun the payment notification pluralizes ("3 hours"), which is the
 * vocabulary existing rows already hold.
 */
export const PayoutUnit = {
  HOUR: "hour",
  TASK: "task",
  SUBMISSION: "submission",
} as const;
export type PayoutUnit = (typeof PayoutUnit)[keyof typeof PayoutUnit];

/** Picker order: the two project-based units, then the one-time default. */
export const PAYOUT_UNITS = [
  PayoutUnit.HOUR,
  PayoutUnit.TASK,
  PayoutUnit.SUBMISSION,
] as const;

/** Whether a stored unit is one of the shortlist rather than a custom one. */
export function isPayoutUnit(value: string): value is PayoutUnit {
  return (PAYOUT_UNITS as readonly string[]).includes(value);
}

/**
 * Names a unit where no quantity sits beside it to fix its number. The row it
 * prices can be one or many, so the label carries both.
 */
export const PAYOUT_UNIT_LABELS: Record<PayoutUnit, string> = {
  [PayoutUnit.HOUR]: "hour(s)",
  [PayoutUnit.TASK]: "task(s)",
  [PayoutUnit.SUBMISSION]: "submission(s)",
};

/**
 * A quantity read against its unit: "3 hours", "1 task".
 *
 * Takes the stored unit rather than a PayoutUnit because a legacy row, the
 * timesheet CSV and the break-glass payout task can all carry a word from
 * outside the set, and one already plural ("hours") is what turned a bare `+ "s"`
 * into "3 hourss" on an expert's payment notification.
 */
export function formatPayoutQuantity(quantity: number, unit: string): string {
  const noun = quantity === 1 || unit.endsWith("s") ? unit : `${unit}s`;
  return `${quantity} ${noun}`;
}

/**
 * The unit a study's pay frequency prices in. ONE_TIME is a fixed amount for the
 * whole engagement, so its unit is the submission that engagement delivers.
 */
export function payoutUnitForPayRateFrequency(
  payRateFrequency: StudyPayRateFrequency,
): PayoutUnit {
  if (payRateFrequency === StudyPayRateFrequency.HOURLY) {
    return PayoutUnit.HOUR;
  }
  if (payRateFrequency === StudyPayRateFrequency.PER_TASK) {
    return PayoutUnit.TASK;
  }
  return PayoutUnit.SUBMISSION;
}

export const InvoiceCadence = {
  WEEKLY: "WEEKLY",
  MONTHLY: "MONTHLY",
} as const;
export type InvoiceCadence =
  (typeof InvoiceCadence)[keyof typeof InvoiceCadence];

export const DEFAULT_INVOICE_CADENCE = "WEEKLY" as const;
export const DEFAULT_INVOICE_PAYMENT_TERMS_DAYS = 30;

export const InvitationEventType = {
  SENT: "SENT",
  RE_SENT: "RE_SENT",
  VIEWED: "VIEWED",
  ACCEPTED: "ACCEPTED",
  DECLINED: "DECLINED",
  RESCINDED: "RESCINDED",
  EXPIRED: "EXPIRED",
  COMPLETED: "COMPLETED",
  REMINDER_SENT: "REMINDER_SENT",
} as const;
export type InvitationEventType =
  (typeof InvitationEventType)[keyof typeof InvitationEventType];

export const ActorType = {
  RESEARCHER: "RESEARCHER",
  PANELIST: "PANELIST",
  SYSTEM: "SYSTEM",
} as const;
export type ActorType = (typeof ActorType)[keyof typeof ActorType];

export const DeclineReason = {
  PRICE_TOO_LOW: "PRICE_TOO_LOW",
  NOT_INTERESTED: "NOT_INTERESTED",
  TIMING_BAD: "TIMING_BAD",
  TOO_BUSY: "TOO_BUSY",
  NOT_QUALIFIED: "NOT_QUALIFIED",
  TOPIC_NOT_RELEVANT: "TOPIC_NOT_RELEVANT",
  PREFER_DIFFERENT_FORMAT: "PREFER_DIFFERENT_FORMAT",
  TECHNICAL_ISSUES: "TECHNICAL_ISSUES",
  OTHER: "OTHER",
  NOT_PROVIDED: "NOT_PROVIDED",
} as const;
export type DeclineReason = (typeof DeclineReason)[keyof typeof DeclineReason];

// Single label source for DeclineReason: the expert-facing decline picker and
// the internal comms log must show the same words.
export const DECLINE_REASON_LABELS: Record<DeclineReason, string> = {
  PRICE_TOO_LOW: "Payment amount is too low",
  NOT_INTERESTED: "Not interested in this topic",
  TIMING_BAD: "Bad timing / too busy",
  TOO_BUSY: "Too busy",
  NOT_QUALIFIED: "I don't feel qualified",
  TOPIC_NOT_RELEVANT: "Topic not relevant to me",
  PREFER_DIFFERENT_FORMAT: "Prefer a different format",
  TECHNICAL_ISSUES: "Technical issues",
  OTHER: "Other reason",
  NOT_PROVIDED: "Not provided",
};

// Extraction & Processing
export const ExtractionMethod = {
  USER_PROVIDED: "USER_PROVIDED",
  LLM_EXTRACTED: "LLM_EXTRACTED",
  INFERRED: "INFERRED",
  THIRD_PARTY: "THIRD_PARTY",
  MANUAL: "MANUAL",
} as const;
export type ExtractionMethod =
  (typeof ExtractionMethod)[keyof typeof ExtractionMethod];

/**
 * Evaluation outcome for insight observations.
 *
 * Represents the result of evaluating an insight against interview data.
 * This is an OUTCOME type, not a process status - use separate mechanisms
 * to track in-progress states (e.g., checking if visualTranscript exists).
 */
export const EvaluationOutcome = {
  ANSWERED: "ANSWERED",
  NOT_APPLICABLE: "NOT_APPLICABLE",
  NO_EVIDENCE: "NO_EVIDENCE",
  NOT_ASKED: "NOT_ASKED",
} as const;
export type EvaluationOutcome =
  (typeof EvaluationOutcome)[keyof typeof EvaluationOutcome];

/**
 * @deprecated Use EvaluationOutcome instead. Kept for backwards compatibility during migration.
 */
export const AnswerStatus = EvaluationOutcome;
/**
 * @deprecated Use EvaluationOutcome instead.
 */
export type AnswerStatus = EvaluationOutcome;

// Panel & Study Config
export const PanelStatus = {
  DRAFT: "DRAFT",
  READY: "READY",
} as const;
export type PanelStatus = (typeof PanelStatus)[keyof typeof PanelStatus];

export const PanelRecruitmentStatus = {
  ACTIVE: "ACTIVE",
  PAUSED: "PAUSED",
  STOPPED: "STOPPED",
} as const;
export type PanelRecruitmentStatus =
  (typeof PanelRecruitmentStatus)[keyof typeof PanelRecruitmentStatus];

export const PanelMemberSource = {
  PROLIFIC: "PROLIFIC",
  MANUAL_ADDITION: "MANUAL_ADDITION",
  INTERVIEW_APPROVAL: "INTERVIEW_APPROVAL",
  INTERNAL_PANEL_SEARCH: "INTERNAL_PANEL_SEARCH",
  REFERRAL: "REFERRAL",
} as const;
export type PanelMemberSource =
  (typeof PanelMemberSource)[keyof typeof PanelMemberSource];

/**
 * Reason why a panel member is considered a perfect match
 *
 * This enum replaces the logic of checking sources and match scores.
 * The reason is computed when a panel member is created/updated.
 *
 * Note: REFERRAL source is NOT automatically a perfect match.
 * Referrals must meet algorithmic criteria or be manually validated.
 */
export const PerfectMatchReason = {
  ALGORITHMIC_MATCH: "ALGORITHMIC_MATCH", // matchScore === totalCriteria (100% match)
  MANUAL_VALIDATION: "MANUAL_VALIDATION", // Human approved (interview approval, manual add)
  EXTERNAL_PROVIDER: "EXTERNAL_PROVIDER", // Sourced from external provider
  REFERRAL_VALIDATED: "REFERRAL_VALIDATED", // Referral validated by referral system
  LEGACY: "LEGACY", // Backward compatibility (addedById exists, source null)
  INTERVIEW_PASSED: "INTERVIEW_PASSED", // Real-time screening passed during interview
} as const;
export type PerfectMatchReason =
  (typeof PerfectMatchReason)[keyof typeof PerfectMatchReason];

export const InsightDisplayPolicy = {
  SHOWN: "SHOWN",
  HIDDEN: "HIDDEN",
  RESTRICTED: "RESTRICTED",
} as const;
export type InsightDisplayPolicy =
  (typeof InsightDisplayPolicy)[keyof typeof InsightDisplayPolicy];

export const InsightEvaluationType = {
  TRANSCRIPT: "TRANSCRIPT", // Evaluate using audio/text transcript (default)
  VISUAL: "VISUAL", // Evaluate using visual grading data
} as const;
export type InsightEvaluationType =
  (typeof InsightEvaluationType)[keyof typeof InsightEvaluationType];

export const InsightOriginType = {
  DYNAMICALLY_GENERATED: "DYNAMICALLY_GENERATED", // Created by research guides
  TERAC_APPROVED: "TERAC_APPROVED", // Global insights maintained by Terac
} as const;
export type InsightOriginType =
  (typeof InsightOriginType)[keyof typeof InsightOriginType];

export const AnalysisToggle = {
  ENABLED: "ENABLED",
  DISABLED: "DISABLED",
} as const;
export type AnalysisToggle =
  (typeof AnalysisToggle)[keyof typeof AnalysisToggle];

export const FeatureToggle = {
  ENABLED: "ENABLED",
  DISABLED: "DISABLED",
} as const;
export type FeatureToggle = (typeof FeatureToggle)[keyof typeof FeatureToggle];

export const ProjectVisibility = {
  STANDARD: "STANDARD",
  INTERNAL: "INTERNAL",
} as const;
export type ProjectVisibility =
  (typeof ProjectVisibility)[keyof typeof ProjectVisibility];

// Interview Interactions
export const InterviewInteractionType = {
  SCREEN_SHARE_STARTED: "SCREEN_SHARE_STARTED",
  SCREEN_SHARE_STOPPED: "SCREEN_SHARE_STOPPED",
  CAMERA_STARTED: "CAMERA_STARTED",
  CAMERA_STOPPED: "CAMERA_STOPPED",
  MIC_MUTED: "MIC_MUTED",
  MIC_ENABLED: "MIC_ENABLED",
  VIDEO_MUTED: "VIDEO_MUTED",
  VIDEO_ENABLED: "VIDEO_ENABLED",
  RESEARCHER_MESSAGE_INJECTED: "RESEARCHER_MESSAGE_INJECTED",
  STIMULUS_SHOWN: "STIMULUS_SHOWN",
  INTERVIEW_STARTED_BY_AGENT: "INTERVIEW_STARTED_BY_AGENT",
  INTERVIEW_ENDED_BY_AGENT: "INTERVIEW_ENDED_BY_AGENT",
  INTERVIEW_ENDED_BY_USER: "INTERVIEW_ENDED_BY_USER",
  INTERVIEW_ENDED_BY_RESEARCHER: "INTERVIEW_ENDED_BY_RESEARCHER",
  INPUT_TEXT_SUBMITTED: "INPUT_TEXT_SUBMITTED",
  INPUT_FILE_UPLOADED: "INPUT_FILE_UPLOADED",
  COPY_REFERRAL_LINK: "COPY_REFERRAL_LINK",
  USER_AUTHENTICATED: "USER_AUTHENTICATED",
  INTERVIEWER_MUTED: "INTERVIEWER_MUTED",
  INTERVIEWER_UNMUTED: "INTERVIEWER_UNMUTED",
  PARTICIPANT_END_WARNING_GIVEN: "PARTICIPANT_END_WARNING_GIVEN",
  REFERRAL_LINK_CREATED: "REFERRAL_LINK_CREATED",
} as const;
export type InterviewInteractionType =
  (typeof InterviewInteractionType)[keyof typeof InterviewInteractionType];

export const InteractionProcessingStatus = {
  PENDING: "PENDING",
  PROCESSING: "PROCESSING",
  COMPLETED: "COMPLETED",
  FAILED: "FAILED",
} as const;
export type InteractionProcessingStatus =
  (typeof InteractionProcessingStatus)[keyof typeof InteractionProcessingStatus];

// Panel Feedback
export const PanelFeedbackType = {
  INVITATION_FEEDBACK: "INVITATION_FEEDBACK",
  NPS: "NPS",
} as const;
export type PanelFeedbackType =
  (typeof PanelFeedbackType)[keyof typeof PanelFeedbackType];

export const InvitationFeedbackReason = {
  PAY_TOO_LOW: "PAY_TOO_LOW",
  TOPIC_NOT_INTERESTING: "TOPIC_NOT_INTERESTING",
  TOO_LONG: "TOO_LONG",
  BAD_TIMING: "BAD_TIMING",
  TECHNICAL_ISSUES: "TECHNICAL_ISSUES",
  OTHER: "OTHER",
} as const;
export type InvitationFeedbackReason =
  (typeof InvitationFeedbackReason)[keyof typeof InvitationFeedbackReason];

export const RECRUITMENT_FEES = {
  USER_INTERVIEWS_B2B_FEE_USD: 20,
  USER_INTERVIEWS_B2C_FEE_USD: 10,
  USER_INTERVIEWS_PROCESSING_RATE: 0.03,
  // TODO: Update when partnership rates are negotiated with Maven
  MAVEN_FEE_USD: 0,
  MANUAL_RECRUITER_HOURLY_RATE: 9.62, // $30,000/year / (60 hrs/week * 52 weeks)
} as const;

// Study Costs
export const CostSourceType = {
  API_DIRECT: "API_DIRECT",
  DERIVED: "DERIVED",
  ALLOCATED: "ALLOCATED",
} as const;
export type CostSourceType =
  (typeof CostSourceType)[keyof typeof CostSourceType];

// Recruitment
export const RecruitmentChannel = {
  INTERNAL_PANEL: "INTERNAL_PANEL",
  INTERNAL_INFERRED: "INTERNAL_INFERRED",
  PROLIFIC: "PROLIFIC",
  USER_INTERVIEWS: "USER_INTERVIEWS",
  MAVEN: "MAVEN",
  RESPONDENT: "RESPONDENT",
  EMPORIA: "EMPORIA",
  PURE_PROFILE: "PURE_PROFILE",
  NEWTON: "NEWTON",
  REDDIT: "REDDIT",
  FACEBOOK: "FACEBOOK",
  FACEBOOK_ADS: "FACEBOOK_ADS",
  FACEBOOK_DM: "FACEBOOK_DM",
  GOOGLE_ADS: "GOOGLE_ADS",
  LINKEDIN: "LINKEDIN",
  LINKEDIN_ADS: "LINKEDIN_ADS",
  LINKEDIN_JOBS: "LINKEDIN_JOBS",
  LINKEDIN_DM: "LINKEDIN_DM",
  TWITTER: "TWITTER",
  EMAIL_CAMPAIGN: "EMAIL_CAMPAIGN",
  EMAIL_OUTREACH: "EMAIL_OUTREACH",
  DISCORD: "DISCORD",
  MANUAL_INVITE: "MANUAL_INVITE",
  REFERRAL: "REFERRAL",
  PUBLIC_LINK: "PUBLIC_LINK",
  EVENT_QR: "EVENT_QR",
  WEBSITE_EMBED: "WEBSITE_EMBED",
  NETQUEST: "NETQUEST",
  CINT: "CINT",
  SMARTLEAD: "SMARTLEAD",
  LINQ_MESSAGES: "LINQ_MESSAGES",
  /**
   * The expert agent in the browser. Its own channel rather than reusing
   * `LINQ_MESSAGES`, because a conversion from the app is not a text: a
   * measurement week comparing the two surfaces cannot read them mixed.
   *
   * Named for the actor and the surface. `LINQ_MESSAGES` is the same axis under
   * an older name -- the only thing that mints one is the expert agent -- so a
   * third surface belongs here as `EXPERT_AGENT_<surface>`, not `<X>_MESSAGES`.
   */
  EXPERT_AGENT_WEB: "EXPERT_AGENT_WEB",
  OTHER: "OTHER",
} as const;
export type RecruitmentChannel =
  (typeof RecruitmentChannel)[keyof typeof RecruitmentChannel];

/**
 * Channels where participants are paid through the external platform's
 * own system. Terac must NOT issue a ledger payout for these participants.
 * This is the single source of truth; used by both interview-level and
 * submission-level payout tasks.
 */
export const EXTERNAL_PAYMENT_CHANNELS: ReadonlySet<RecruitmentChannel> =
  new Set([
    RecruitmentChannel.PROLIFIC,
    RecruitmentChannel.USER_INTERVIEWS,
    RecruitmentChannel.MAVEN,
    RecruitmentChannel.RESPONDENT,
    RecruitmentChannel.EMPORIA,
    RecruitmentChannel.PURE_PROFILE,
    RecruitmentChannel.NEWTON,
  ]);

// NOTE: external-panel detection (`isExternalPanelPaid`, the raw-param marker
// keys, redirect URLs) now lives in the canonical registry at
// `@repo/common/external-panels`, which imports the channel set above. Do not
// reintroduce that logic here; `@repo/db` is the data layer. Whether a link
// admits a participant without an account is NOT part of that registry: it is
// per-link state, on `StudyRecruitmentLink.loginRequirement` below.

export const RecruitmentLinkStatus = {
  ACTIVE: "ACTIVE",
  PAUSED: "PAUSED",
  COMPLETED: "COMPLETED",
  REVOKED: "REVOKED",
} as const;
export type RecruitmentLinkStatus =
  (typeof RecruitmentLinkStatus)[keyof typeof RecruitmentLinkStatus];

export const RecruitmentLinkPaymentType = {
  PAID: "PAID",
  UNPAID: "UNPAID",
} as const;
export type RecruitmentLinkPaymentType =
  (typeof RecruitmentLinkPaymentType)[keyof typeof RecruitmentLinkPaymentType];

export const RecruitmentLinkType = {
  DEFAULT: "DEFAULT",
  CUSTOM: "CUSTOM",
} as const;
export type RecruitmentLinkType =
  (typeof RecruitmentLinkType)[keyof typeof RecruitmentLinkType];

export const GeoblockStatus = {
  ENABLED: "ENABLED",
  DISABLED: "DISABLED",
} as const;
export type GeoblockStatus =
  (typeof GeoblockStatus)[keyof typeof GeoblockStatus];

export const TestModeStatus = {
  ENABLED: "ENABLED",
  DISABLED: "DISABLED",
} as const;
export type TestModeStatus =
  (typeof TestModeStatus)[keyof typeof TestModeStatus];

/**
 * Whether a recruitment link requires a Terac login to enter the apply flow.
 * ANONYMOUS_ALLOWED lets external-panel traffic enter without an account (the
 * panel already identified the participant). Default REQUIRED preserves the
 * normal sign-up/conversion gate for every other link.
 */
export const LoginRequirement = {
  REQUIRED: "REQUIRED",
  ANONYMOUS_ALLOWED: "ANONYMOUS_ALLOWED",
} as const;
export type LoginRequirement =
  (typeof LoginRequirement)[keyof typeof LoginRequirement];

// RBAC - Roles and Scopes
export type MemberRole = "owner" | "admin" | "member";

export type OrganizationScope =
  | "viewOrganization"
  | "viewBilling"
  | "addCredits"
  | "manageInvitations"
  | "editSettings"
  | "viewIntegrations"
  | "viewActivity"
  | "launchStudies"
  | "pauseStudies"
  | "stopStudies"
  | "deleteStudies"
  | "editStudies"
  | "reviewInterviews"
  | "deleteOrganization"
  | "transferOwnership"
  | "removeAdmins"
  | "removeMembers"
  | "changeAdminRole"
  | "changeMemberRole"
  | "createProject"
  | "editProject"
  | "deleteProject"
  | "viewSSO"
  | "manageSSO"
  | "manageWebhooks";

export const ALL_SCOPES: OrganizationScope[] = [
  "viewOrganization",
  "viewBilling",
  "addCredits",
  "manageInvitations",
  "editSettings",
  "viewIntegrations",
  "viewActivity",
  "launchStudies",
  "pauseStudies",
  "stopStudies",
  "deleteStudies",
  "editStudies",
  "reviewInterviews",
  "deleteOrganization",
  "transferOwnership",
  "removeAdmins",
  "removeMembers",
  "changeAdminRole",
  "changeMemberRole",
  "createProject",
  "editProject",
  "deleteProject",
  "viewSSO",
  "manageSSO",
  "manageWebhooks",
];

// Scopes available to admins (subset of owner scopes)
const ADMIN_SCOPES: OrganizationScope[] = [
  "viewOrganization",
  "viewBilling",
  "addCredits",
  "manageInvitations",
  "editSettings",
  "viewIntegrations",
  "viewActivity",
  "launchStudies",
  "pauseStudies",
  "stopStudies",
  "deleteStudies",
  "editStudies",
  "reviewInterviews",
  "removeMembers",
  "changeMemberRole",
  "createProject",
  "editProject",
  "deleteProject",
  "viewSSO",
  "manageSSO",
  "manageWebhooks",
];

// Default role-scope mappings (used for seeding)
export const DEFAULT_ROLE_SCOPES: Record<MemberRole, OrganizationScope[]> = {
  owner: ALL_SCOPES,
  admin: ADMIN_SCOPES,
  member: ["viewOrganization"],
};

// ============================================================================
// LEDGER SYSTEM - Double-Entry Bookkeeping
// ============================================================================

export const LedgerAccountType = {
  ORGANIZATION: "ORGANIZATION",
  STUDY: "STUDY",
  PARTICIPANT: "PARTICIPANT",
  PLATFORM_FEE: "PLATFORM_FEE",
  FREE_MONEY_POOL: "FREE_MONEY_POOL",
  MONEY_IN: "MONEY_IN",
  MONEY_OUT: "MONEY_OUT",
} as const;
export type LedgerAccountType =
  (typeof LedgerAccountType)[keyof typeof LedgerAccountType];

export const LEDGER_SYSTEM_ACCOUNTS = {
  PLATFORM_FEE: "system_platform_fee",
  FREE_MONEY_POOL: "system_free_money_pool",
  MONEY_IN: "system_money_in",
  MONEY_OUT: "system_money_out",
} as const;

export const LedgerTransactionType = {
  TOPUP: "TOPUP",
  INVOICE_CREDIT: "INVOICE_CREDIT",
  STUDY_CHARGE: "STUDY_CHARGE",
  EARNING: "EARNING",
  EXTERNAL_PAYOUT: "EXTERNAL_PAYOUT",
  WITHDRAWAL: "WITHDRAWAL",
  REFUND: "REFUND",
  ADJUSTMENT: "ADJUSTMENT",
  REFERRAL_COMMISSION: "REFERRAL_COMMISSION",
  CLAWBACK: "CLAWBACK",
  PAYOUT_FUNDING: "PAYOUT_FUNDING",
} as const;
export type LedgerTransactionType =
  (typeof LedgerTransactionType)[keyof typeof LedgerTransactionType];

// Insight Types
// Single source of truth for insight data types used across the platform
export const INSIGHT_TYPES = [
  "text",
  "numeric",
  "enum",
  "multi_enum",
  "boolean",
  "date",
  "float",
] as const;
export type InsightType = (typeof INSIGHT_TYPES)[number];

/**
 * Subset of insight types commonly used in AI-generated insights.
 * Excludes 'numeric' (use 'float' instead) and 'boolean' (rarely needed for AI output).
 */
export const AI_INSIGHT_TYPES = [
  "text",
  "float",
  "enum",
  "multi_enum",
  "date",
] as const;
export type AIInsightType = (typeof AI_INSIGHT_TYPES)[number];

// Opportunity Listings
export const OpportunityListingStatus = {
  GENERATING: "GENERATING",
  PUBLISHED: "PUBLISHED",
  UNPUBLISHED: "UNPUBLISHED",
} as const;
export type OpportunityListingStatus =
  (typeof OpportunityListingStatus)[keyof typeof OpportunityListingStatus];

export const PayRateFrequency = {
  PER_SESSION: "PER_SESSION",
  HOURLY: "HOURLY",
  FIXED: "FIXED",
} as const;
export type PayRateFrequency =
  (typeof PayRateFrequency)[keyof typeof PayRateFrequency];

export const WorkMode = {
  REMOTE: "REMOTE",
  HYBRID: "HYBRID",
  ON_SITE: "ON_SITE",
} as const;
export type WorkMode = (typeof WorkMode)[keyof typeof WorkMode];

export const OpportunityType = {
  INTERVIEW: "INTERVIEW",
  SURVEY: "SURVEY",
  USABILITY_TEST: "USABILITY_TEST",
  FOCUS_GROUP: "FOCUS_GROUP",
  OTHER: "OTHER",
} as const;
export type OpportunityType =
  (typeof OpportunityType)[keyof typeof OpportunityType];

// Calendar Integration (generic; supports Google, future: Outlook, Apple)
export const CalendarConnectionStatus = {
  CONNECTED: "CONNECTED",
  DISCONNECTED: "DISCONNECTED",
  ERROR: "ERROR",
  REVOKED: "REVOKED",
} as const;
export type CalendarConnectionStatus =
  (typeof CalendarConnectionStatus)[keyof typeof CalendarConnectionStatus];

export const CalendarSyncStatus = {
  PENDING: "PENDING",
  SYNCING: "SYNCING",
  COMPLETED: "COMPLETED",
  FAILED: "FAILED",
} as const;
export type CalendarSyncStatus =
  (typeof CalendarSyncStatus)[keyof typeof CalendarSyncStatus];

export const CalendarContactSource = {
  CALENDAR: "CALENDAR",
  CONTACTS: "CONTACTS",
} as const;
export type CalendarContactSource =
  (typeof CalendarContactSource)[keyof typeof CalendarContactSource];

export const CalendarProvider = {
  GOOGLE: "GOOGLE",
} as const;
export type CalendarProvider =
  (typeof CalendarProvider)[keyof typeof CalendarProvider];

export const PayoutStatus = {
  NOT_APPLICABLE: "NOT_APPLICABLE",
  PENDING: "PENDING",
  PAID: "PAID",
  FAILED: "FAILED",
} as const;
export type PayoutStatus = (typeof PayoutStatus)[keyof typeof PayoutStatus];

// Interview Quality & Satisfaction

export const SessionDisconnectSource = {
  AGENT_ENDED: "AGENT_ENDED",
  USER_ENDED: "USER_ENDED",
  USER_DECLINED_NEXT: "USER_DECLINED_NEXT",
  CONNECTION_LOST: "CONNECTION_LOST",
  UNKNOWN: "UNKNOWN",
} as const;
export type SessionDisconnectSource =
  (typeof SessionDisconnectSource)[keyof typeof SessionDisconnectSource];

export const SatisfactionSurveyStatus = {
  SUBMITTED: "SUBMITTED",
  SKIPPED: "SKIPPED",
  TIMED_OUT: "TIMED_OUT",
} as const;
export type SatisfactionSurveyStatus =
  (typeof SatisfactionSurveyStatus)[keyof typeof SatisfactionSurveyStatus];

export const SatisfactionSurveySource = {
  AGENT_INITIATED: "AGENT_INITIATED",
  USER_DISCONNECT: "USER_DISCONNECT",
} as const;
export type SatisfactionSurveySource =
  (typeof SatisfactionSurveySource)[keyof typeof SatisfactionSurveySource];

// Hard Filters
export const HardFilterType = {
  // Phase 1
  AGE_RANGE: "AGE_RANGE",
  COUNTRY_OF_RESIDENCE: "COUNTRY_OF_RESIDENCE",
  COUNTRY_OF_IP: "COUNTRY_OF_IP",
  STATE_PROVINCE: "STATE_PROVINCE",
  CITY: "CITY",
  LANGUAGE: "LANGUAGE",
  HAS_TAKEN_STUDY: "HAS_TAKEN_STUDY",
  HAS_NOT_TAKEN_STUDY: "HAS_NOT_TAKEN_STUDY",
  // Phase 2: Professional (LinkedIn job function codes + industry sector groups)
  JOB_FUNCTION: "JOB_FUNCTION",
  INDUSTRY: "INDUSTRY",
  SENIORITY: "SENIORITY",
  COMPANY_SIZE: "COMPANY_SIZE",
  WORK_SETTING: "WORK_SETTING",
  EMPLOYMENT_STATUS: "EMPLOYMENT_STATUS",
  // Phase 2: Demographic
  SEX: "SEX",
  GENDER: "GENDER",
  HOUSEHOLD_INCOME: "HOUSEHOLD_INCOME",
  LEVEL_OF_EDUCATION: "LEVEL_OF_EDUCATION",
  MARITAL_STATUS: "MARITAL_STATUS",
  CHILDREN: "CHILDREN",
  HOME_OWNER: "HOME_OWNER",
  ETHNICITY: "ETHNICITY",
  COLLAR_TYPE: "COLLAR_TYPE",
} as const;
export type HardFilterType =
  (typeof HardFilterType)[keyof typeof HardFilterType];

// Submission Lifecycle
export const SubmissionState = {
  INIT: "INIT",
  SCREENING: "SCREENING",
  SCREEN_PASSED: "SCREEN_PASSED",
  SCREENED_OUT: "SCREENED_OUT",
  RESERVED: "RESERVED",
  IN_PROGRESS: "IN_PROGRESS",
  AWAITING_REVIEW: "AWAITING_REVIEW",
  APPROVED: "APPROVED",
  REJECTED: "REJECTED",
  REPORTED: "REPORTED",
  RETURNED: "RETURNED",
  ABANDONED: "ABANDONED",
  TIMED_OUT: "TIMED_OUT",
  CANCELLED: "CANCELLED",
  FAILED: "FAILED",
} as const;
export type SubmissionState =
  (typeof SubmissionState)[keyof typeof SubmissionState];

export const ScreeningStatus = {
  PENDING: "PENDING",
  IN_PROGRESS: "IN_PROGRESS",
  ACTIVE: "ACTIVE",
  PROCESSING: "PROCESSING",
  AWAITING_REVIEW: "AWAITING_REVIEW",
  // Customer-review gate: the internal (Terac/judge) review passed and the
  // step is now parked for the customer to approve/reject before the expert
  // is invited. Only reached when the study opts into customer screening
  // review; sequential after AWAITING_REVIEW. See ENG-808.
  AWAITING_CUSTOMER_REVIEW: "AWAITING_CUSTOMER_REVIEW",
  RESERVED: "RESERVED",
  PASSED: "PASSED",
  FAILED: "FAILED",
  SKIPPED: "SKIPPED",
  QUALITY_CHECK_FAILED: "QUALITY_CHECK_FAILED",
} as const;
export type ScreeningStatus =
  (typeof ScreeningStatus)[keyof typeof ScreeningStatus];

export const ScreeningType = {
  FORM_SCREENING: "FORM_SCREENING",
  AI_SCREENING: "AI_SCREENING",
  FILE_UPLOAD: "FILE_UPLOAD",
} as const;
export type ScreeningType = (typeof ScreeningType)[keyof typeof ScreeningType];

/**
 * Why a Terac reviewer rejected a screening at the internal gate. The
 * `ScreeningRejectReason` pgEnum is built from this tuple, so any change to
 * the codes needs a migration; stored tags are codes, never labels.
 */
export const SCREENING_REJECT_REASONS = [
  "READING_OR_AI_ASSIST",
  "IDENTITY_MISMATCH",
  "MISREPRESENTED_CLAIMS",
  "CRITERIA_NOT_MET",
  "LOW_QUALITY_ANSWERS",
  "CAMERA_OR_SETTING_ISSUE",
  "OTHER",
] as const;
export type ScreeningRejectReason = (typeof SCREENING_REJECT_REASONS)[number];

export const SCREENING_REJECT_REASON_LABELS: Record<
  ScreeningRejectReason,
  string
> = {
  READING_OR_AI_ASSIST: "Reading a script or using AI live",
  IDENTITY_MISMATCH: "Identity doesn't match or can't be verified",
  MISREPRESENTED_CLAIMS: "Claims contradict their verified profile",
  CRITERIA_NOT_MET: "Doesn't meet the study's criteria or experience",
  LOW_QUALITY_ANSWERS: "Vague, shallow or off-topic answers",
  CAMERA_OR_SETTING_ISSUE: "Camera, audio or setting issue",
  OTHER: "Other (note required)",
};

/** The reasons serious enough to offer "Reject and ban" in the reject dialog. */
export const GRAVE_SCREENING_REJECT_REASONS: readonly ScreeningRejectReason[] =
  ["MISREPRESENTED_CLAIMS"];

export function hasGraveScreeningRejectReason(
  tags: readonly ScreeningRejectReason[],
): boolean {
  return tags.some((tag) => GRAVE_SCREENING_REJECT_REASONS.includes(tag));
}

/**
 * Hard cap on free-text screener answers (selectionType: OPEN_TEXT).
 * Single source of truth; used by `formScreeningAnswerSchema.freeText.max(...)`,
 * the `saveScreeningAnswer` router input, and the textarea UI in
 * `ScreeningStepFormCard`. Bumping it here updates all three layers.
 */
export const OPEN_TEXT_MAX_CHARS = 2000;

/**
 * Longest comment staff can leave about an expert, after trimming. The
 * `SubmissionComment_body_length` CHECK repeats the literal.
 */
export const SUBMISSION_COMMENT_MAX_LENGTH = 2000;

/**
 * Hard cap on the verbatim inbound a screener answer was mapped from.
 * Single source of truth: `formScreeningAnswerSchema.evidence.max(...)`
 * rejects the row on read, so `saveScreeningAnswer` truncates to the same
 * number on write rather than persisting one the parser will refuse.
 */
export const EVIDENCE_MAX_CHARS = 500;

export const SubmissionTrigger = {
  SYSTEM: "SYSTEM",
  USER: "USER",
  AGENT: "AGENT",
  ADMIN: "ADMIN",
  SCHEDULER: "SCHEDULER",
} as const;
export type SubmissionTrigger =
  (typeof SubmissionTrigger)[keyof typeof SubmissionTrigger];

export const SubmissionActorType = {
  USER: "USER",
  AGENT: "AGENT",
  SYSTEM: "SYSTEM",
  ADMIN: "ADMIN",
} as const;
export type SubmissionActorType =
  (typeof SubmissionActorType)[keyof typeof SubmissionActorType];

export const SubmissionTaskType = {
  INTERVIEW: "INTERVIEW",
  SURVEY: "SURVEY",
  FILE_UPLOAD: "FILE_UPLOAD",
  ACTIVITY: "ACTIVITY",
  SCHEDULED_INTERVIEW: "SCHEDULED_INTERVIEW",
  AGREEMENT: "AGREEMENT",
  JOIN_SLACK: "JOIN_SLACK",
  SLACK_HANDOFF: "SLACK_HANDOFF",
  E_SIGNATURE: "E_SIGNATURE",
  INSTALL_TIME_TRACKER: "INSTALL_TIME_TRACKER",
} as const;
export type SubmissionTaskType =
  (typeof SubmissionTaskType)[keyof typeof SubmissionTaskType];

export const BoldsignSignatureRequestStatus = {
  PENDING: "PENDING",
  SIGNED: "SIGNED",
  DECLINED: "DECLINED",
  EXPIRED: "EXPIRED",
  REVOKED: "REVOKED",
} as const;
export type BoldsignSignatureRequestStatus =
  (typeof BoldsignSignatureRequestStatus)[keyof typeof BoldsignSignatureRequestStatus];

export const SubmissionTaskStatus = {
  PENDING: "PENDING",
  ACTIVE: "ACTIVE",
  PROCESSING: "PROCESSING",
  AWAITING_REVIEW: "AWAITING_REVIEW",
  APPROVED: "APPROVED",
  REJECTED: "REJECTED",
  TIMED_OUT: "TIMED_OUT",
  FAILED: "FAILED",
  SUPERSEDED: "SUPERSEDED",
  QUALITY_CHECK_FAILED: "QUALITY_CHECK_FAILED",
  ABANDONED: "ABANDONED",
} as const;
export type SubmissionTaskStatus =
  (typeof SubmissionTaskStatus)[keyof typeof SubmissionTaskStatus];

export const ReviewType = {
  AUTO_APPROVE: "AUTO_APPROVE",
  MANUAL_REVIEW: "MANUAL_REVIEW",
  SELF_REPORT: "SELF_REPORT",
} as const;
export type ReviewType = (typeof ReviewType)[keyof typeof ReviewType];

/**
 * May a completion signal alone approve this task, or does a human owe a
 * verdict first?
 *
 * A completion redirect says the work was DONE, never that it was acceptable,
 * and the signal is an unauthenticated GET the participant's own browser makes.
 * So only the explicitly self-approving methods may arm payout from it;
 * MANUAL_REVIEW and anything added later must land in AWAITING_REVIEW.
 *
 * Lives here, next to the enum, because two independent paths act on this rule:
 * the callback route and the stale-callback sweep. They disagreed once (VERIA-1;
 * the sweep kept auto-approving MANUAL_REVIEW work six hours after #8482 fixed
 * the route), so the rule is a function rather than a condition to re-type. A
 * new ReviewType is opt-in: it needs adding here to become self-approving.
 */
export function mayCompletionSignalApprove(reviewType: ReviewType): boolean {
  return (
    reviewType === ReviewType.AUTO_APPROVE ||
    reviewType === ReviewType.SELF_REPORT
  );
}

// Per-study applicant-review mode (ENG-808). MANUAL_REVIEW routes a screening
// that cleared Terac's internal gate to AWAITING_CUSTOMER_REVIEW so the customer
// approves/rejects before the expert is invited; AUTO_INVITE passes straight
// through. Modeled as a mode (not a boolean) so future review tiers (e.g. a
// premium review with PII access) slot in without a schema change. Independent
// of the internal gate.
export const CustomerScreeningReview = {
  AUTO_INVITE: "AUTO_INVITE",
  MANUAL_REVIEW: "MANUAL_REVIEW",
} as const;
export type CustomerScreeningReview =
  (typeof CustomerScreeningReview)[keyof typeof CustomerScreeningReview];

export const SourceEventType = {
  SCREENING: "SCREENING",
  TASK: "TASK",
  SYSTEM: "SYSTEM",
} as const;
export type SourceEventType =
  (typeof SourceEventType)[keyof typeof SourceEventType];

export const TaskObservationStatus = {
  ACTIVE: "ACTIVE",
  COMPLETED: "COMPLETED",
  ABANDONED: "ABANDONED",
  FAILED: "FAILED",
} as const;
export type TaskObservationStatus =
  (typeof TaskObservationStatus)[keyof typeof TaskObservationStatus];

export const ScheduledInterviewBookingStatus = {
  PENDING: "PENDING",
  AWAITING_BOOKING: "AWAITING_BOOKING",
  SCHEDULED: "SCHEDULED",
} as const;
export type ScheduledInterviewBookingStatus =
  (typeof ScheduledInterviewBookingStatus)[keyof typeof ScheduledInterviewBookingStatus];

export const ScheduledInterviewBookingSource = {
  POSTMESSAGE: "POSTMESSAGE",
  MANUAL_FALLBACK: "MANUAL_FALLBACK",
} as const;
export type ScheduledInterviewBookingSource =
  (typeof ScheduledInterviewBookingSource)[keyof typeof ScheduledInterviewBookingSource];

export const ScheduledInterviewCalendarOwner = {
  RESEARCHER: "RESEARCHER",
  EXPERT: "EXPERT",
} as const;
export type ScheduledInterviewCalendarOwner =
  (typeof ScheduledInterviewCalendarOwner)[keyof typeof ScheduledInterviewCalendarOwner];

export const SchedulingProvider = {
  CALENDLY: "CALENDLY",
  GOOGLE_CALENDAR: "GOOGLE_CALENDAR",
  MICROSOFT_BOOKING: "MICROSOFT_BOOKING",
  // Researcher pastes an arbitrary https link (Cal.com, SavvyCal, ...). No host
  // allowlist and no completion callback, so the expert self-reports. Blocked
  // on expert write paths: researcher-provided only.
  CUSTOM: "CUSTOM",
} as const;
export type SchedulingProvider =
  (typeof SchedulingProvider)[keyof typeof SchedulingProvider];

// Single source of truth for human-readable provider names. Used by launch
// validation, the live-edit gate, audit-trail labels, and recruiter-facing
// error messages so the same name shows everywhere. The exhaustive switch
// forces every new provider to declare its label here at compile time.
export function schedulingProviderLabel(provider: SchedulingProvider): string {
  switch (provider) {
    case SchedulingProvider.CALENDLY:
      return "Calendly";
    case SchedulingProvider.GOOGLE_CALENDAR:
      return "Google Calendar";
    case SchedulingProvider.MICROSOFT_BOOKING:
      return "Microsoft Bookings";
    case SchedulingProvider.CUSTOM:
      return "Custom link";
  }
}

// Human-readable list of the accepted hosts per provider, mirrored from the
// allowlist in `isProviderEventTypeUrl`. Used only in validation-failure
// messages so a recruiter who pastes a rejected URL sees which hosts pass.
// `schedulingProviderLabel` stays the bare name for audit/UI. Keep these two
// in lockstep with the switch below when adding a provider or host.
export function schedulingProviderHostHint(
  provider: SchedulingProvider,
): string {
  switch (provider) {
    case SchedulingProvider.CALENDLY:
      return "calendly.com";
    case SchedulingProvider.GOOGLE_CALENDAR:
      return "calendar.app.google or calendar.google.com";
    case SchedulingProvider.MICROSOFT_BOOKING:
      return "outlook.office365.com, outlook.office.com, bookings.microsoft.com, or bookings.cloud.microsoft";
    // Phrased as an adjective so it reads correctly interpolated into
    // "Must be a ${x} link." (-> "Must be a secure (https) link.").
    case SchedulingProvider.CUSTOM:
      return "secure (https)";
  }
}

// Example booking URL per provider, used as the recruiter-facing placeholder in
// the task editors so the Event URL field mirrors the format expected for the
// selected provider. Single source of truth shared by the v1 task-plan editor
// and the v2 create flow; the exhaustive switch forces every new provider to
// declare an example here at compile time.
export function schedulingProviderPlaceholder(
  provider: SchedulingProvider,
): string {
  switch (provider) {
    case SchedulingProvider.CALENDLY:
      return "https://calendly.com/your-team/30min";
    case SchedulingProvider.GOOGLE_CALENDAR:
      return "https://calendar.app.google/...";
    case SchedulingProvider.MICROSOFT_BOOKING:
      return "https://outlook.office365.com/owa/calendar/.../bookings/";
    case SchedulingProvider.CUSTOM:
      return "https://...";
  }
}

// Host allowlist for SCHEDULED_INTERVIEW URLs, branched on provider.
// Source of truth; the recruiter UI consumes this for inline validation
// feedback AND the Zod schema in `task-plan.ts` calls it as the security
// boundary for anything that bypasses the editor (programmatic study
// create, future API surface, tampered request).
export function isProviderEventTypeUrl(
  url: string,
  provider: SchedulingProvider,
): boolean {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  if (parsed.protocol !== "https:") {
    return false;
  }
  switch (provider) {
    case SchedulingProvider.CALENDLY:
      return (
        parsed.hostname === "calendly.com" ||
        parsed.hostname.endsWith(".calendly.com")
      );
    case SchedulingProvider.GOOGLE_CALENDAR:
      return (
        parsed.hostname === "calendar.app.google" ||
        parsed.hostname === "calendar.google.com"
      );
    case SchedulingProvider.MICROSOFT_BOOKING:
      // Microsoft Bookings public booking pages live under the Outlook web
      // hosts (legacy `/owa/calendar/.../bookings/` and `.../book/...`
      // forms) and redirect to the canonical `bookings.cloud.microsoft`
      // host, which serves the embeddable page (CSP `frame-ancestors
      // https:`, no X-Frame-Options). We accept the pasted form and the
      // canonical host; the iframe follows the redirect chain itself.
      // Tenant-specific vanity hosts are not covered; add them here if a
      // customer surfaces one.
      return (
        parsed.hostname === "outlook.office365.com" ||
        parsed.hostname === "outlook.office.com" ||
        parsed.hostname === "bookings.microsoft.com" ||
        parsed.hostname === "bookings.cloud.microsoft"
      );
    // Trust the researcher's link: any host is allowed. The https gate above
    // is the only boundary (custom links are never fetched or framed server-side,
    // only opened in the expert's own browser tab).
    case SchedulingProvider.CUSTOM:
      return true;
  }
}

// "Terac owns this booking": it reads the expert's own Google Calendar and
// creates the event itself instead of framing a provider-hosted page. Legacy
// task plans omit both keys, and an absent calendarOwner means RESEARCHER (see
// types/task-plan.ts), never EXPERT. Widened to string because tRPC clients
// hold the same pair as plain strings.
export function isNativeGoogleExpertScheduling(
  provider: string | null | undefined,
  calendarOwner: string | null | undefined,
): boolean {
  return (
    provider === SchedulingProvider.GOOGLE_CALENDAR &&
    calendarOwner === ScheduledInterviewCalendarOwner.EXPERT
  );
}

/**
 * A ScheduledInterview row Terac books itself. The pair above is not enough on
 * a row: `provideExpertLink` overwrites `provider` with whatever the expert
 * picked, and an expert on any EXPERT-owner study may pick Google Calendar and
 * share an appointment-schedule link. `expertProvidedAt` is what separates the
 * two, and nothing ever stamps it on a Terac-booked row.
 *
 * `bookingStatus` is the other half, and it is what a row's birth records. A
 * Terac-booked row is created AWAITING_BOOKING, because the expert's calendar
 * connection is already its link; a link-share row is created at the column
 * default, PENDING, and only reaches AWAITING_BOOKING when the expert shares.
 * The same (GOOGLE_CALENDAR, EXPERT) study materializes the second shape
 * whenever `terac-booked-scheduling` reads false - which includes every read
 * that merely failed, since the flag client swallows errors into false. Without
 * PENDING excluded here, such a row becomes unschedulable from both sides the
 * moment the flag turns on: the expert's share is refused as Terac-booked while
 * PENDING is the only status that share can leave, and the researcher's picker
 * offers slots that every booking then rejects.
 */
export function isNativeScheduledInterviewRow(row: {
  provider: string | null | undefined;
  calendarOwner: string | null | undefined;
  expertProvidedAt: Date | string | null | undefined;
  bookingStatus: string | null | undefined;
}): boolean {
  return (
    isNativeGoogleExpertScheduling(row.provider, row.calendarOwner) &&
    (row.expertProvidedAt ?? null) === null &&
    row.bookingStatus !== ScheduledInterviewBookingStatus.PENDING
  );
}

/**
 * A plan item Terac books on an org member's own calendar. Deliberately not the
 * (GOOGLE_CALENDAR, RESEARCHER) pair: that pair already means "the researcher
 * pasted a Google appointment-schedule link" on live studies, and an absent
 * calendarOwner decays to it. The host id is the marker.
 */
export function isResearcherHostScheduling(item: {
  provider?: string | null;
  calendarOwner?: string | null;
  calendarHostUserId?: string | null;
}): boolean {
  return (
    Boolean(item.calendarHostUserId) &&
    (item.calendarOwner ?? ScheduledInterviewCalendarOwner.RESEARCHER) ===
      ScheduledInterviewCalendarOwner.RESEARCHER &&
    item.provider === SchedulingProvider.GOOGLE_CALENDAR
  );
}

/**
 * The row form. `calendarHostUserId` is only ever written by the host-mode
 * materialize branch, so its presence is the whole test.
 */
export function isResearcherHostRow(row: {
  calendarHostUserId?: string | null;
}): boolean {
  return Boolean(row.calendarHostUserId);
}

/**
 * Task statuses a Terac-booked interview can still be booked on. The task has
 * not run yet (that is the point), so anything past ACTIVE means the booking
 * would land on work already done, removed from the plan, or abandoned.
 */
export const TERAC_BOOKABLE_TASK_STATUSES: readonly SubmissionTaskStatus[] = [
  SubmissionTaskStatus.PENDING,
  SubmissionTaskStatus.ACTIVE,
];

/**
 * Submission states where the candidate is still in flight. A Terac-booked
 * interview is booked before the interview happens, so the submission may still
 * be screening; everything else is screened out, rejected, abandoned or already
 * finished.
 */
export const TERAC_BOOKABLE_SUBMISSION_STATES: readonly SubmissionState[] = [
  SubmissionState.SCREENING,
  SubmissionState.SCREEN_PASSED,
  SubmissionState.RESERVED,
  SubmissionState.IN_PROGRESS,
];

/**
 * Whether an interview on this task can still be booked at all. Neither a plan
 * edit nor a dead submission tears down the task's ScheduledInterview row, so
 * an unbooked row outlives both and only this says it is stale. It leaves out
 * the screening-review gate on purpose: that one is "not yet", not "never".
 */
export function isSchedulingTaskInFlight(input: {
  taskStatus: string;
  submissionState: string;
}): boolean {
  return (
    TERAC_BOOKABLE_TASK_STATUSES.some((s) => s === input.taskStatus) &&
    TERAC_BOOKABLE_SUBMISSION_STATES.some((s) => s === input.submissionState)
  );
}

// Task plan types (JSONB shape for studies.taskPlan)

interface TaskPlanItemBase {
  id?: string;
  sequence: number;
  taskType: SubmissionTaskType;
  reviewType: ReviewType;
  title?: string;
  description?: string;
  taskUrl?: string;
  durationMinutes?: number;
  availableAfterSequence?: number;
  availableAfterDelayMinutes?: number;
}

export interface InterviewTaskPlanItem extends TaskPlanItemBase {
  taskType: "INTERVIEW";
  discussionGuideMarkdown?: string;
}

export interface SurveyTaskPlanItem extends TaskPlanItemBase {
  taskType: "SURVEY";
  surveyId: string;
}

export interface FileUploadTaskPlanItem extends TaskPlanItemBase {
  taskType: "FILE_UPLOAD";
  instructions?: string;
  // Every config field is optional so the builder can autosave a task the
  // moment it is added, before the author has filled anything in (same
  // rationale as `eventTypeUrl` / `templateId`). `study-lifecycle-service`
  // requires instructions, at least one accepted type and a coherent
  // minFiles/maxFiles range before the study can go live. Readers fill the
  // gaps from DEFAULT_FILE_UPLOAD_* below rather than treating absent as zero.
  acceptedMimeTypes?: string[];
  minFiles?: number;
  maxFiles?: number;
  maxFileSizeBytes?: number;
}

export interface ActivityTaskPlanItem extends TaskPlanItemBase {
  taskType: "ACTIVITY";
}

export interface ScheduledInterviewTaskPlanItem extends TaskPlanItemBase {
  taskType: "SCHEDULED_INTERVIEW";
  // Optional in the type so legacy task plans (saved before the multi-provider
  // field landed) parse cleanly. The Zod schema fills in CALENDLY.
  provider?: SchedulingProvider;
  // Optional so legacy plans parse; absent = RESEARCHER. EXPERT keeps eventTypeUrl empty.
  calendarOwner?: ScheduledInterviewCalendarOwner;
  // The org member whose Google Calendar hosts these interviews. Present = the
  // expert books a real slot off that calendar and Terac creates the event;
  // absent = the researcher-pasted link flow. Never inferred: the schema has no
  // "the researcher" (Study has no createdById; internalOwnerId and
  // internalEngineerId are Terac staff; launchedById is whoever clicked Launch).
  calendarHostUserId?: string;
  // The user-pasted booking URL; either a Calendly event URL or a Google
  // Calendar appointment-schedule URL depending on `provider`.
  eventTypeUrl: string;
}

// Booking-URL gate shared by the launch gate (study-lifecycle-service) and the
// pre-recruitment convert gate. Returns an error message, or null if the task
// may go live. EXPERT tasks carry no researcher URL (the expert supplies it at
// task time), so they always pass. Save-time leniency (empty URL allowed) is a
// separate concern handled in the task-plan Zod refine, not here.
/**
 * The launch-blocking issue with a FILE_UPLOAD task, or null when it is ready.
 *
 * One predicate rather than one per surface: the schema leaves every config
 * field optional so the editor can autosave a freshly-added task, which makes
 * this the only thing standing between a half-filled card and a live study
 * whose drop zone accepts nothing. Shared by the launch gate and the builder's
 * Continue gate, so the two cannot disagree about what "ready" means.
 *
 * Deliberately stricter than `resolveFileUploadTaskConfig`, which is the read
 * side and clamps rather than refuses: launch refuses, readers cope.
 */
export function validateFileUploadForLaunch(
  item: FileUploadTaskPlanItem,
): string | null {
  if (!item.instructions?.trim()) {
    return `Task ${item.sequence} (file upload) is missing instructions telling the expert what to upload.`;
  }
  if (!item.acceptedMimeTypes?.length) {
    return `Task ${item.sequence} (file upload) needs at least one accepted file type.`;
  }
  // Reachable mid-edit and harmless to a reader (the resolver clamps), but a
  // live study with this pair leaves an expert who can never satisfy submit.
  const minFiles = item.minFiles ?? DEFAULT_FILE_UPLOAD_MIN_FILES;
  const maxFiles = item.maxFiles ?? DEFAULT_FILE_UPLOAD_MAX_FILES;
  if (maxFiles < minFiles) {
    return `Task ${item.sequence} (file upload) allows at most ${maxFiles} file(s) but requires ${minFiles}.`;
  }
  return null;
}

export function validateScheduledInterviewForLaunch(
  item: ScheduledInterviewTaskPlanItem,
): string | null {
  if (item.calendarOwner === ScheduledInterviewCalendarOwner.EXPERT) {
    return null;
  }
  // Host mode books on the named member's calendar, so no URL is required. A
  // pasted one is still the degraded fallback and still has to be valid; the
  // shape rules below carry that, only the missing-URL refusal is dropped.
  if (item.calendarHostUserId) {
    if (item.provider !== SchedulingProvider.GOOGLE_CALENDAR) {
      return `Task ${item.sequence} (scheduled interview) must use Google Calendar to book on your calendar.`;
    }
    if (
      item.eventTypeUrl &&
      !isProviderEventTypeUrl(item.eventTypeUrl, item.provider)
    ) {
      return `Task ${item.sequence} (scheduled interview) has an invalid ${schedulingProviderLabel(item.provider)} URL (${schedulingProviderHostHint(item.provider)}).`;
    }
    return null;
  }
  const provider = item.provider ?? SchedulingProvider.CALENDLY;
  const label = schedulingProviderLabel(provider);
  if (!item.eventTypeUrl) {
    return `Task ${item.sequence} (scheduled interview) is missing a ${label} URL.`;
  }
  if (!isProviderEventTypeUrl(item.eventTypeUrl, provider)) {
    return `Task ${item.sequence} (scheduled interview) has an invalid ${label} URL (${schedulingProviderHostHint(provider)}).`;
  }
  return null;
}

export interface AgreementSection {
  id: string;
  eyebrow: string;
  title: string;
  body: string;
  // Legacy per-section checkbox label. Acknowledge tasks replace the checkbox
  // with a typed-phrase gate, so this is no longer shown; kept optional so
  // task plans saved before the redesign still parse.
  confirm?: string;
  // The literal line the participant must type to confirm THIS section
  // (per-step, GitHub delete-repo style). Author-configurable; falls back to
  // the section eyebrow at read time when unset.
  confirmPhrase?: string;
  highlight?: { value: string; label: string };
}

export interface AgreementTaskPlanItem extends TaskPlanItemBase {
  taskType: "AGREEMENT";
  sections: AgreementSection[];
  fullTermsUrl?: string;
}

export interface JoinSlackTaskPlanItem extends TaskPlanItemBase {
  taskType: "JOIN_SLACK";
  workspaceName: string;
  workspaceUrl: string;
  inviteUrl?: string;
}

export interface SlackHandoffTaskPlanItem extends TaskPlanItemBase {
  taskType: "SLACK_HANDOFF";
  channelName?: string;
  welcomeFrom?: string;
  openSlackUrl?: string;
  headsUp?: string;
}

/**
 * The task whose presence turns Insightful time tracking on for a study, and
 * which an expert clears only by installing the tracker and starting it.
 *
 * Both refs mirror the Insightful ids the study was provisioned with. Optional
 * because a task exists from the moment it is added to a plan, which is before
 * anything has been created in Insightful, and because leaving them optional is
 * what keeps the new type additive: no stored plan fails to parse.
 */
export interface InstallTimeTrackerTaskPlanItem extends TaskPlanItemBase {
  taskType: "INSTALL_TIME_TRACKER";
  trackerProjectRef?: string;
  trackerTaskRef?: string;
}

export interface ESignatureTaskPlanItem extends TaskPlanItemBase {
  taskType: "E_SIGNATURE";
  templateId: string;
  // Optional name of the role on the BoldSign template that the expert
  // signs as. Defaults to the first role (roleIndex 1) when absent.
  signerRole?: string;
  fullTermsUrl?: string;
}

/**
 * BoldSign template for the default independent-contractor agreement. It backs
 * the "Contractor agreement" choice in the E_SIGNATURE task's template selector
 * and is what AI-training projects sign today; an author can still point at a
 * different BoldSign template via "Custom template ID" in the editor. Kept here
 * so the study builder and the AI-training seed (StudyDraftService) share one
 * id instead of hardcoding it in two places.
 */
export const CONTRACTOR_AGREEMENT_TEMPLATE_ID =
  "9a23583b-bbf0-4697-845b-53b293ff3a0c";

export interface ESignatureTemplateOption {
  /** Stable key for the selector. */
  value: string;
  /** Label shown in the dropdown. */
  label: string;
  /** BoldSign template id this option writes to the task's templateId. */
  templateId: string;
}

/**
 * Named templates offered in the E_SIGNATURE "contract template" selector. Only
 * the contractor default exists today; the editor adds a "Custom template ID"
 * escape hatch for any other BoldSign template.
 */
export const ESIGNATURE_TEMPLATE_OPTIONS: readonly ESignatureTemplateOption[] =
  [
    {
      value: "contractor",
      label: "Contractor agreement",
      templateId: CONTRACTOR_AGREEMENT_TEMPLATE_ID,
    },
  ];

/**
 * Default titles for the seeded AI-training tasks. Every task must have a title
 * before a study can launch (see builder validation), so seeding them keeps a
 * freshly-created AI project one edit away from launch (the Slack invite URL)
 * instead of blocked on empty titles.
 */
export const DEFAULT_AI_TRAINING_AGREEMENT_TITLE = "Project agreement";
export const DEFAULT_ESIGNATURE_TASK_TITLE = "Sign your contract";
export const DEFAULT_JOIN_SLACK_TASK_TITLE = "Join the project Slack";
export const DEFAULT_FILE_UPLOAD_TASK_TITLE = "Upload your files";

/**
 * The default title and body for an `INSTALL_TIME_TRACKER` task.
 *
 * A body, unlike every other default above, because this step has no control on
 * it. The expert cannot mark it done and neither can anyone else, so the copy
 * is the whole screen: it has to explain that mail from a company they have
 * never heard of is genuine, ask them to start a manual timer rather than only
 * install an app, and say that the step opens on its own once the tracker has
 * reported. An author can replace it per study.
 *
 * Every recording claim below is read off the shared-settings record each
 * provisioned expert is created under, `GET /shared-settings/w8rvowl6pwzdg42`,
 * confirmed 2026-09-01: `type: "manual"` (they start and stop it),
 * `screenRecording.enabled: false` (no video), `idleScreenshots: false`
 * (nothing while idle). The record's `screenshots: 30` carries no unit, so the
 * copy states no capture frequency at all. If that record changes, this copy is
 * no longer verified and has to be rechecked before it keeps shipping.
 */
export const DEFAULT_INSTALL_TIME_TRACKER_TASK_TITLE =
  "Set up your time tracker";

/**
 * Stands in for the address Insightful mailed, substituted at render.
 *
 * The body is a stored string, so the address cannot be interpolated where it
 * is written. It is named inline rather than added as a trailing sentence
 * because the address only means anything next to the instruction to go and
 * read that inbox. A screen with no address to substitute drops the
 * parenthetical and the line still reads.
 */
export const INSTALL_TIME_TRACKER_EMAIL_TOKEN = "{email}";

/**
 * Path to the expert-facing guide for the tracker step.
 *
 * A path, not a URL: the host comes from `getAppUrl()` at the call site, so a
 * preview deployment links its own copy of the guide instead of sending a
 * reviewer to production.
 *
 * Named in the body rather than left to an author, because the step asks
 * someone to install software from a company they have not heard of and then
 * choose the right project inside it. Every question ops answered by hand this
 * week is on that page: the invitation goes to a personal address and expires
 * after 48 hours, the download works without it, and an empty project list
 * means they were never added rather than that they did something wrong.
 */
export const INSIGHTFUL_EXPERT_DOCS_PATH =
  "/docs/experts/working/time-tracking";
/**
 * Written as our own action rather than as a warning about an unexpected
 * sender, so the paragraph of reassurance an earlier draft needed ("it is
 * real", "it comes from them rather than from us") has nothing left to do.
 *
 * "Added", not "invited", and the mail is a conditional rather than an
 * instruction. Creating the Insightful employee is what sends the invitation,
 * and an expert who did an hourly study before already has one: reaching a
 * second study's tracker task adds them to the new project and mails nothing.
 * Telling that person to go and check their inbox sends them looking for
 * something that is not there. Both readings are true as written: a first
 * timer has a mail that just arrived, and a returning expert has one from
 * whenever they were first provisioned.
 *
 * "You start and stop the timer yourself" is the one reassurance kept, because
 * it is the only line telling someone their screen is not watched all day. It
 * is read off `type: "manual"` on `GET /shared-settings/w8rvowl6pwzdg42`, the
 * record every provisioned expert is created under. If that configuration
 * changes, this sentence is no longer verified and must be rechecked before it
 * keeps shipping.
 */
export const DEFAULT_INSTALL_TIME_TRACKER_TASK_BODY = `We've added you to this project in Insightful, the app that tracks the hours you work on it.

Your invitation is in your personal email (${INSTALL_TIME_TRACKER_EMAIL_TOKEN}). Accept it, install the app, then pick this project and start the timer when you begin working.`;

/**
 * The same step for an expert who has been through it before.
 *
 * The conditional above is the best a single body can do, and it is still the
 * wrong shape for a returning expert: it puts an inbox first for someone whose
 * inbox has nothing new in it. No mail is sent the second time, because
 * creating the Insightful employee is what sends one and theirs already
 * exists. So this says that outright rather than leaving them to infer it
 * from "if you don't have it yet".
 *
 * It names no project. The Insightful project carries the study's
 * `internalName`, which is the customer codename, so a "pick X" instruction
 * cannot be written here without showing an expert something internal.
 *
 * Chosen by `returning` on the task config, which is derived from the expert's
 * earlier tracker task rows rather than from their worker ref. See
 * `hasEarlierTrackerTask`.
 */
export const RETURNING_INSTALL_TIME_TRACKER_TASK_BODY = `You already have Insightful from an earlier study, so there's no new invitation this time.

We've added this project to your account. Open the app, pick it from your project list, and start the timer.`;

/** Title for the same step, for an expert who already has the app. */
export const RETURNING_INSTALL_TIME_TRACKER_TASK_TITLE =
  "Track this project in Insightful";

/**
 * Bodies this repository has shipped as the default for the tracker step.
 *
 * The builder copies the default body into the study's own plan item when the
 * task is added, so by the time anyone reads it back there is no way to tell an
 * author's deliberate wording from the text we put there ourselves. Treating
 * every stored body as an override is what made the returning copy unreachable:
 * a screen told an expert who already had Insightful to go and check their
 * inbox, above a button that opened the app.
 *
 * Matching against this list is what separates the two. A body that equals
 * something we shipped is ours to replace; anything else was written by an
 * author and is left alone.
 *
 * Append, never edit. An entry that leaves this list starts being mistaken for
 * authored copy on every study that stored it, and those studies are still
 * live.
 */
export const SHIPPED_INSTALL_TIME_TRACKER_TASK_BODIES: readonly string[] = [
  DEFAULT_INSTALL_TIME_TRACKER_TASK_BODY,
  // Shipped 2026-09-03 to 2026-09-04, and live on main until this change.
  // Enumerated the three actions, which the guide now carries: the list and
  // the link were saying the same thing in two places.
  `We've added you to this project in Insightful, the app that tracks the hours you work on it.

1. Accept the invitation in your personal email (${INSTALL_TIME_TRACKER_EMAIL_TOKEN})
2. Install the app
3. Start the timer when you begin working`,
  // Shipped 2026-09-02 to 2026-09-03. Hedged the mail into a conditional,
  // which reads as neither one thing nor the other to a returning expert.
  `We've added you to this project in Insightful, the app that tracks the hours you work on it.

If you don't have it yet, the invitation is in your personal email. Accept it and install the app. You start and stop the timer yourself.

Then mark this step done.`,
  // Shipped until 2026-09-02. Promised a mail unconditionally, which is the
  // complaint the returning body answers, so a study still holding this one
  // needs replacing more than any other.
  `We've invited you to this project in Insightful, the app that tracks the hours you work on it.

Check your personal email for the invitation, accept it, and install the app. You start and stop the timer yourself.

Then mark this step done.`,
];

/** Titles this repository has shipped as the default for the tracker step. */
export const SHIPPED_INSTALL_TIME_TRACKER_TASK_TITLES: readonly string[] = [
  DEFAULT_INSTALL_TIME_TRACKER_TASK_TITLE,
];

/**
 * Opens the desktop agent. `insightful://` and `workpuls://` are both
 * registered by the agent at startup (`app.setAsDefaultProtocolClient`, no
 * platform guard, so Windows gets it through the registry and macOS through
 * `CFBundleURLTypes`); verified against agent 8.1.15, where macOS
 * LaunchServices reports the binding.
 *
 * The path is deliberately empty. The agent's handler splits the URL into a
 * command and one argument and emits it, and the only commands with a listener
 * are `login-token`, `quit`, `sso-error` and `update-finalize`. There is no
 * `project` command, so a link naming one would open the agent and drop the
 * rest: the same outcome as this, with a promise attached that the agent
 * cannot keep.
 */
export const INSIGHTFUL_OPEN_APP_URL = "insightful://";

/**
 * Where a first timer goes when the invitation never arrived.
 *
 * Insightful's invitation links expire after 48 hours, and an expert who
 * reaches this step days after being provisioned has a dead one. Sign-in with
 * "Forgot password" is the self-service way out: it mails the same address and
 * sets a credential, so it recovers an expired invitation and a lost one alike.
 */
export const INSIGHTFUL_SIGN_IN_URL = "https://app.insightful.io/";

/**
 * Where a returning expert gets the installer if the app is gone from this
 * machine. Behind the Insightful login, which is the right gate: this is only
 * ever offered to someone whose account already exists.
 */
export const INSIGHTFUL_DOWNLOAD_URL =
  "https://app.insightful.io/#/token/download";

/** The task's page inside its project in the Insightful app. */
export function insightfulProjectUrl(
  projectRef: string,
  taskRef: string,
): string {
  return `https://app.insightful.io/#/app/project/${projectRef}/${taskRef}`;
}

/**
 * The one Gather room every opportunity shares.
 *
 * Fixed platform-wide rather than per study: there is a single space experts
 * drop into, so storing it once per handbook would be the same URL copied into
 * every row and one typo away from sending somebody nowhere. A study that ever
 * needs its own still overrides it with a `GATHER` communication link, which is
 * what the expert opportunity home reads first.
 *
 * `guest=true` is load-bearing: an expert has no Gather account, and without it
 * the link asks them to make one before they can reach the room.
 */
export const TERAC_GATHER_WORKSPACE_URL =
  "https://app.v2.gather.town/app/3ae1bde2-3210-4863-90bc-ae8f554b6b9d/join?guest=true&copysource=inviteTeamModal&copierid=014afd56-04a1-4b1a-ab4d-f7955d8ebc7c";

/**
 * Fallbacks for a FILE_UPLOAD task whose config fields are absent. Every
 * reader (the presign use-case, the submit gate, the expert card and the
 * launch gate) resolves through these, so an under-specified plan behaves
 * like a single any-type upload rather than rejecting every file or
 * accepting an unbounded one.
 */
export const DEFAULT_FILE_UPLOAD_ACCEPTED_MIME_TYPES: readonly string[] = [
  "*/*",
];
export const DEFAULT_FILE_UPLOAD_MIN_FILES = 1;
export const DEFAULT_FILE_UPLOAD_MAX_FILES = 1;
/** 25 MB. Presigned PUT is a single request, so this is also the practical ceiling. */
export const DEFAULT_FILE_UPLOAD_MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024;
/**
 * Hard ceiling on `maxFiles`, matching the builder's stepper. Bounded because
 * the presign gate counts a task's existing files on every upload, so an
 * unbounded limit from the API or the copilot would make one upload session
 * quadratic in the number of files.
 */
export const FILE_UPLOAD_TASK_MAX_FILES_LIMIT = 20;

/**
 * What an operator may attach to a study handbook, and how large.
 *
 * Here rather than beside the presigner in `packages/api` because the editor's
 * drop zone checks the same two facts before it asks for a URL, and reaching
 * `upload-utils` from a client component drags the whole AWS SDK into the
 * browser bundle. Written out rather than derived from the interview-upload
 * set: an allowlist reads better as a list, and what a handbook accepts is not
 * bound to what an interview does.
 *
 * The video formats are in because `terac-video` plays an uploaded file in a
 * `<video>` element, so the formats that element can play have to be
 * uploadable.
 */
export const HANDBOOK_ASSET_CONTENT_TYPES: ReadonlySet<string> = new Set([
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "text/plain",
  "text/csv",
  "text/markdown",
  "application/json",
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/heic",
  "video/mp4",
  "video/webm",
  "video/quicktime",
  "audio/mpeg",
  "audio/mp4",
  "audio/wav",
]);

export function isAllowedHandbookAssetContentType(
  contentType: string,
): boolean {
  return HANDBOOK_ASSET_CONTENT_TYPES.has(contentType);
}

/**
 * 200 MB. A presigned PUT cannot enforce a length it did not sign, so this is
 * what the editor refuses a selection over and what the mutation refuses a
 * declared size over. Deliberately far below the 2 GB interview ceiling: this
 * is a reference document or a walkthrough clip, not a raw recording.
 */
export const MAX_HANDBOOK_ASSET_SIZE = 200 * 1024 * 1024;

/** A FILE_UPLOAD task's config with every optional field resolved. */
export interface ResolvedFileUploadTaskConfig {
  instructions: string;
  acceptedMimeTypes: string[];
  minFiles: number;
  maxFiles: number;
  maxFileSizeBytes: number;
}

/**
 * Read a FILE_UPLOAD plan item as concrete limits.
 *
 * The presign gate, the submit gate and the expert card must agree on what
 * counts as an acceptable file; resolving in three places is how they drift
 * into a card that offers an upload the server then refuses. `maxFiles` is
 * clamped up to `minFiles` because a half-edited plan can carry max < min and
 * that pair makes the submit range unsatisfiable.
 */
export function resolveFileUploadTaskConfig(
  item: Pick<
    FileUploadTaskPlanItem,
    | "instructions"
    | "acceptedMimeTypes"
    | "minFiles"
    | "maxFiles"
    | "maxFileSizeBytes"
  >,
): ResolvedFileUploadTaskConfig {
  const minFiles = item.minFiles ?? DEFAULT_FILE_UPLOAD_MIN_FILES;
  return {
    instructions: item.instructions ?? "",
    acceptedMimeTypes: item.acceptedMimeTypes?.length
      ? [...item.acceptedMimeTypes]
      : [...DEFAULT_FILE_UPLOAD_ACCEPTED_MIME_TYPES],
    minFiles,
    maxFiles: Math.max(
      item.maxFiles ?? DEFAULT_FILE_UPLOAD_MAX_FILES,
      minFiles,
    ),
    maxFileSizeBytes:
      item.maxFileSizeBytes ?? DEFAULT_FILE_UPLOAD_MAX_FILE_SIZE_BYTES,
  };
}

/**
 * Whether `contentType` satisfies an accepted-types list, supporting exact
 * types, family wildcards (`image/*`) and the any-type wildcard. Shared by the
 * presign gate and the expert card so the client never offers a file the
 * server then refuses.
 */
export function isAcceptedUploadMimeType(
  contentType: string,
  acceptedMimeTypes: readonly string[],
): boolean {
  return acceptedMimeTypes.some((accepted) => {
    if (accepted === "*/*") {
      return true;
    }
    if (accepted.endsWith("/*")) {
      return contentType.startsWith(accepted.slice(0, -1));
    }
    return contentType === accepted;
  });
}

/**
 * Default acknowledgement sections seeded into the AGREEMENT task for
 * AI-training projects, so a researcher starts from a filled, editable
 * agreement instead of a blank form. Modelled on the acknowledgements real
 * projects use (how work is set up, volume, payment, timeline, next steps):
 * each is a first-person point the expert types `confirmPhrase` to confirm they
 * understand. These carry no id; a fresh one is generated per study at seed
 * time (see StudyDraftService.seedTaskPlan and the editor's makeTask).
 */
export const DEFAULT_AI_TRAINING_AGREEMENT_SECTIONS: readonly Omit<
  AgreementSection,
  "id"
>[] = [
  {
    eyebrow: "How the work is set up",
    title: "I'll do the work through Slack and our partner platform.",
    body: "Terac handles recruiting, coordination, support, and payouts. After onboarding you'll get access and detailed task instructions in Slack. The tasks themselves may run on a partner platform we'll point you to.",
    confirmPhrase: "I understand how the work is set up",
  },
  {
    eyebrow: "Project volume",
    title: "I expect to put in at least 5-10 hours per week.",
    body: "This project expects a steady, consistent time commitment. Actual volume depends on the project pipeline and the quality of your submissions, and joining does not guarantee a fixed number of hours.",
    confirmPhrase: "I agree with this project volume",
  },
  {
    eyebrow: "Payment",
    title: "I'll be paid for approved work, every Friday.",
    body: "You're paid at the rate shown in this opportunity. Approvals are visible in your Terac account, and earnings are paid out every Friday for work approved during the preceding week. Work that isn't approved isn't paid.",
    confirmPhrase: "I understand how payment works",
  },
  {
    eyebrow: "Timeline",
    title: "I'm ready to start right away.",
    body: "Work begins as soon as you finish onboarding and join Slack. The sooner you join, the sooner you can start picking up tasks.",
    confirmPhrase: "I understand work starts right away",
  },
  {
    eyebrow: "Next steps",
    title: "I'll join Slack and watch for instructions.",
    body: "After onboarding you'll be added to Slack, where training materials, detailed instructions, task access, and payment confirmations are shared. Keep notifications on and respond quickly to questions or blockers.",
    confirmPhrase: "I understand I need to join Slack for instructions",
  },
];

/**
 * Default copy for the Slack handoff (the final "everything continues in Slack"
 * screen). AI-training projects seed the handoff on by default, and its title
 * and description aren't editable in the folded editor toggle, so these need to
 * read well as-is (the apply component's own fallbacks are interview-flavored).
 * The heads-up is editable via the toggle. See SlackHandoffTask.
 */
export const DEFAULT_SLACK_HANDOFF_TITLE = "You're all set in Slack";
export const DEFAULT_SLACK_HANDOFF_DESCRIPTION =
  "From here on, everything happens in Slack: your task access, instructions, questions, and payout confirmations. There's no separate dashboard to check.";
export const DEFAULT_SLACK_HANDOFF_HEADS_UP =
  "Keep notifications on. The team posts your first tasks and any questions in Slack.";

export type TaskPlanItem =
  | InterviewTaskPlanItem
  | SurveyTaskPlanItem
  | FileUploadTaskPlanItem
  | ActivityTaskPlanItem
  | ScheduledInterviewTaskPlanItem
  | AgreementTaskPlanItem
  | JoinSlackTaskPlanItem
  | SlackHandoffTaskPlanItem
  | ESignatureTaskPlanItem
  | InstallTimeTrackerTaskPlanItem;

// Screening plan types (JSONB shape for studies.screeningPlan)

interface ScreeningPlanItemBase {
  id?: string;
  sequence: number;
  screeningType: ScreeningType;
  reviewType: ReviewType;
  title?: string;
}

export interface FormScreeningPlanItem extends ScreeningPlanItemBase {
  screeningType: "FORM_SCREENING";
  questionIds?: string[];
}

export interface AiScreeningPlanItem extends ScreeningPlanItemBase {
  screeningType: "AI_SCREENING";
}

export interface FileUploadScreeningPlanItem extends ScreeningPlanItemBase {
  screeningType: "FILE_UPLOAD";
  instructions: string;
  acceptedMimeTypes: string[];
  minFiles: number;
  maxFiles: number;
  maxFileSizeBytes?: number;
}

export type ScreeningPlanItem =
  | FormScreeningPlanItem
  | AiScreeningPlanItem
  | FileUploadScreeningPlanItem;

/**
 * Submission states where the participant has actively claimed a task slot.
 * Used by quota enforcement to count toward audience segment capacity.
 * SCREEN_PASSED is excluded: passing screening does not reserve a quota
 * slot; the claim happens at task activation / interview start.
 */
export const CLAIMED_SUBMISSION_STATES = [
  "IN_PROGRESS",
  "AWAITING_REVIEW",
  "APPROVED",
] as const;

/**
 * Thrown (as Error.code) by createOrganizationTransaction's phone-unlock guard
 * when the number already owns an org, so the org.create use-case can map it to
 * the PHONE_ALREADY_USED client error without importing an error class across
 * the repository-module boundary. Lives here so both @repo/db and @repo/api can
 * reference it without a circular dependency.
 */
export const PHONE_ALREADY_UNLOCKED_ERROR = "PHONE_ALREADY_UNLOCKED_ORG";

/**
 * Lowest first. Mirrors `LANGUAGE_PROFICIENCY_LEVELS` in @repo/common/languages,
 * which @repo/db cannot import (circular).
 */
export const LANGUAGE_PROFICIENCY_ORDER = [
  "elementary",
  "limited-working",
  "professional-working",
  "full-professional",
  "native-bilingual",
] as const;
